/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  1. 프로그램명 : 메인프레임
//  2. 프로그램ID : mainFrame.tsx
//  3. 주요설명 : 메인 프레임 구성 요소
//  4. 개발이력 : 2025.08.17  최초개발
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { TextField, MenuItem, Chip, Paper, Tabs, Tab, Table, TableBody, TableRow, TableCell, Dialog, DialogTitle, DialogContent, Checkbox, IconButton } from '@mui/material';
import Tooltip from "@mui/material/Tooltip";import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import DOMPurify from 'dompurify';

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 사용자정의 컴포넌트 import
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { API_BASE_URL } from '../config'; // API 기본 URL
import '../styles/modalMsgBox.css';
import { useCommonTranslation } from '../translations';
import { getUserInfo, clearUserInfo, setLocalStorage, setSolutionTitleCache } from '../utils/userUtils';
import { getClientInfo } from '../utils/clientInfoUtil'; // 로그인 이력용 접속 환경(화면/시간대/언어/플랫폼)
import { getStoredMenuStyle, setStoredMenuStyle, type MenuStyle } from '../utils/menuStyleUtils';
import { getMsgText, getMsgType, getFileIcon, formatFileSize } from '../utils/commonUtils';
import type { ifUserInfo } from '../utils/userUtils';
import { ComponentRenderer, HomeRenderer, DelayedLoading } from '../utils/comptMapUtil';
import IframeTab from '../components/IframeTab'; // 외부URL 메뉴(LINK_KEY='ADDTAB') - xGA 탭 안에 iframe으로 URL을 그대로 띄움
import { useClickOutside } from '../hooks/useClickOutside';
import { pgmIdOf } from '../utils/pgmIdUtil'; // 호출프로그램ID -> 화면ID(메뉴 검색 매칭)
import { useComboBox } from '../hooks/useComboBox'; // 공통코드 콤보(사용자유형 Z0007 명칭 조회용)
import { useTokenExpiry } from '../hooks/useTokenExpiry';
import { useContentScrollButtons, useTabScrollButtons, useSidebarScrollButtons } from '../hooks/useScrollButtons';
import { useTabs, type TabItem } from '../hooks/useTabs';
import ChatWidget from '../components/ChatWidget'; // 플로팅 상담버튼(1:1상담톡 바로가기)
import ScheduleAlert from '../components/ScheduleAlert'; // 일정관리(X3001M1) 알림 토스트
import ChipMsg from "../components/ChipMsg";  // chipMsg 컴포넌트 import
import BackendStatusBadge from '../components/BackendStatusBadge'; // 백엔드 연결상태 표시
import { useCommonPopupModal, useUserPopupModal, useRefPopupModal, useMsgBoxModal } from '../components/CommonModal';// 공통 모달 import
import ChangePasswordModal from '../components/ChangePasswordModal'; // 비밀번호 변경 팝업(LoginPage와 공용)


////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// ag-Grid 필요한 모듈만 등록 : ModuleRegistry
///     .ag-Grid v34 이상에서는 텍스트 셀 에디터(agTextCellEditor)를 사용하려면 TextEditorModule을 등록해야 합니다.
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import ScopeBadge from '../components/ScopeBadge'; // 공유범위 배지(공통코드 X1002)
import { setLicenseReadOnly } from '../utils/licenseUtil'; // 라이선스 읽기 전용 여부(메뉴바 버튼 비활성)
import AgGrid, { selectFirstRow, setInsertRow, setDeleteRow, setUpdateRow, setCopyRow, setCancelRow, setCellValue, getActiveCellValue, getRowCellValue } from "../components/AgGrid";    // agGrid 컴포넌트 import
import {  ModuleRegistry,  ClientSideRowModelModule,  CsvExportModule,  
        TextEditorModule,  TextFilterModule,  NumberEditorModule,  NumberFilterModule,  SelectEditorModule,
        PaginationModule,  ValidationModule,  CellStyleModule,  ColumnAutoSizeModule,  RowStyleModule,  RowSelectionModule,  RowApiModule,
        // 필요시 추가 모듈
        _focusGridInnerElement} from 'ag-grid-community';
ModuleRegistry.registerModules( [
        ClientSideRowModelModule,  CsvExportModule,  
        TextEditorModule,  TextFilterModule,  NumberEditorModule,  NumberFilterModule,  SelectEditorModule,
        PaginationModule,  ValidationModule,  CellStyleModule,  ColumnAutoSizeModule,  RowStyleModule,  RowSelectionModule,  RowApiModule
        // 필요시 추가 모듈
    ]);
    
////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 타입 정의
//  - MainMenuItem : 메인 메뉴 항목
//  - SubMenuItem : 서브 메뉴 항목
//  - TabItem : 탭 항목
////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface MainMenuItem {
  key: string;
  title?: string;
  url: string;
  icon: string;
  hasSubMenu: boolean;
  linkKey?: string; // ZA_MENU.LINK_KEY - 'HOME'이면 홈 화면 메뉴, 하위메뉴 없는 단독 메인메뉴는 'SCHEDULE'/'NOTICE' 등
  pgmId?: string;   // ZA_MENU.CALL_PGM_ID - 홈 메뉴일 때 어떤 홈 컴포넌트를 쓸지(예: 'HomePageV1')
  paramData?: Record<string, any>; // ZA_MENU.PARAM(JSON) - 하위메뉴 없는 단독 메인메뉴가 화면에 넘길 고정 파라미터
}


interface SubMenuData {
  titleKey: string;
  titleName: string;
  icon: string;
  items: SubMenuItem[];
}

interface SubMenuItem {
  itemKey: string;
  itemName?: string;
  icon: string;
  url: string;
  children?: SubMenuItem[];
  autoQuery?: boolean; // JumpIn하는 페이지에서 조회버튼 자동클릭
  paramData?: Record<string, any>; // ZA_MENU.PARAM(JSON) - 같은 화면을 여러 메뉴가 공유할 때 메뉴별 고정 파라미터(예: T1101~1104M1의 보험종류)
  linkKey?: string; // ZA_MENU.LINK_KEY - URL/PGM_ID에 의존하지 않고 "이 메뉴의 시스템상 역할"을 표시(예: 'NOTICE' = 공지사항)
}

// 메뉴 경로 문자열("계약관리 > 계약등록 > 계약등록(장기)") - 메인메뉴부터 메뉴트리(items/children)를 내려가며 itemKey를 찾아
// 거쳐 온 그룹 이름을 모두 이어붙임(2단/3단/그 이상 중첩 모두). 트리에서 못 찾으면 null.
const buildMenuPath = (prefix: string, items: SubMenuItem[], itemKey: string, nameOf: (it: SubMenuItem) => string): string | null => {
  for (const it of items) {
    if (it.itemKey === itemKey) return `${prefix} > ${nameOf(it)}`;
    if (Array.isArray(it.children) && it.children.length > 0) {
      const found = buildMenuPath(`${prefix} > ${nameOf(it)}`, it.children, itemKey, nameOf);
      if (found) return found;
    }
  }
  return null;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 메인컴포넌트 : MainFrame
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const MainFrame: React.FC = () => {
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 공통정의
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const navigate = useNavigate();
  // 상태 관리
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCtrlTabsDropdown, setShowCtrlTabsDropdown] = useState(false);
  const [showNoticeDropdown, setShowNoticeDropdown] = useState(false);
  // 헤더 메뉴 검색 입력값 - 메뉴ID(또는 화면ID)를 넣고 Enter/돋보기 클릭 시 해당 메뉴를 호출함(모바일에서는 숨김)
  const [menuSearch, setMenuSearch] = useState('');
  const [menuSearchOpen, setMenuSearchOpen] = useState(false);   // 자동완성 목록 표시 여부
  const [menuSearchIdx, setMenuSearchIdx] = useState(-1);         // 키보드(↑↓)로 고른 항목
  const menuSearchRef = useRef<HTMLDivElement>(null);             // 검색영역(바깥 클릭 감지용)
  // 탭 드래그앤드롭 상태 - 드래그 중인 탭/드롭 대상 탭의 표시(dragging/drag-over 클래스)를 DOM classList가 아니라 React state로 관리함.
  //  (예전엔 e.target.classList로 직접 붙였다 뗐는데, Edge에서는 드롭 후 탭이 재배치되면서 dragend를 못 받아 dragging 스타일이 남는 현상이 있었음)
  const [dragTabId, setDragTabId] = useState<string | null>(null);
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null);
  const dragFromIndexRef = useRef<number>(-1); // 드래그 중인 탭의 인덱스(dataTransfer 텍스트가 비어도 동작하도록 별도 보관)
  const resetTabDrag = useCallback(() => { setDragTabId(null); setDragOverTabId(null); dragFromIndexRef.current = -1; }, []);
  // dragend가 탭 요소에 전달되지 않는 경우(브라우저별 차이/요소 재배치)에도 드래그 상태가 남지 않도록 window에서도 정리
  useEffect(() => {
    // 버블 단계로 등록 - 탭의 onDrop(React, 루트에서 처리)이 먼저 인덱스를 읽은 뒤에 초기화되어야 함
    window.addEventListener('dragend', resetTabDrag);
    window.addEventListener('drop', resetTabDrag);
    window.addEventListener('pointerdown', resetTabDrag); // 드래그가 어떤 이유로든 끝나지 않고 남아도 다음 클릭에서 정리(새 드래그는 pointerdown 이후 dragstart에서 다시 시작)
    return () => { window.removeEventListener('dragend', resetTabDrag); window.removeEventListener('drop', resetTabDrag); window.removeEventListener('pointerdown', resetTabDrag); };
  }, [resetTabDrag]);
  // 탭관리 우클릭 메뉴 - 헤더의 "탭관리" 드롭다운과 같은 항목을, 활성 탭을 우클릭했을 때 클릭 좌표에도 띄움
  //  (AgGrid.tsx 우클릭메뉴와 동일 패턴). MUI Menu는 바깥 클릭 시 자동으로 닫히므로 별도 useClickOutside/ref 불필요.
  const [tabCtxMenu, setTabCtxMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [pwdChangeOpen, setPwdChangeOpen] = useState(false); // 사용자영역 > 비밀번호변경 팝업(LoginPage와 공용 컴포넌트)
  // 사용자영역 패널(프로필 정보 + 개인정보변경/비밀번호변경/DD생성/로그아웃) 표시용 - showUserDropdown이 true가 될 때 조회
  const [profileData, setProfileData] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  // 메인 메뉴 표시방식(좌측 사이드바 / 상단 메가메뉴) - 사용자가 헤더의 토글 버튼으로 전환, localStorage에 영구 저장
  const [menuStyle, setMenuStyle] = useState<MenuStyle>(() => getStoredMenuStyle());
  const [showMegaMenu, setShowMegaMenu] = useState(false); // 상단 메가메뉴(전체메뉴) 패널 열림 상태 - menuStyle==='topmenu'일 때만 사용
  const megaMenuRef = useRef<HTMLDivElement>(null); // 메가메뉴 영역(바깥 클릭 감지용)
  const userDropdownRef = useRef<HTMLDivElement>(null);       // 사용자 드롭다운 영역(바깥 클릭 감지용)
  const ctrlTabsDropdownRef = useRef<HTMLDivElement>(null);   // 탭관리 드롭다운 영역(바깥 클릭 감지용)
  const noticeDropdownRef = useRef<HTMLDivElement>(null);     // 공지사항 드롭다운 영역(바깥 클릭 감지용)

  // 드롭다운(사용자/탭관리/공지사항) 바깥 영역 클릭 시 닫기
  useClickOutside(userDropdownRef, () => setShowUserDropdown(false), showUserDropdown);
  useClickOutside(menuSearchRef, () => { setMenuSearchOpen(false); setMenuSearchIdx(-1); }, menuSearchOpen);
  useClickOutside(ctrlTabsDropdownRef, () => setShowCtrlTabsDropdown(false), showCtrlTabsDropdown);
  useClickOutside(noticeDropdownRef, () => setShowNoticeDropdown(false), showNoticeDropdown);
  useClickOutside(megaMenuRef, () => setShowMegaMenu(false), showMegaMenu);

  // 공지사항 알림(세션영역 우측 종모양 아이콘) - 신규 공지가 있으면 아이콘 모퉁이에 배지 표시하고,
  // 클릭하면 사용자영역 드롭다운과 동일한 방식(평면 목록 패널)으로 최근 공지 상위 5건을 펼쳐 보여줌.
  // 2026-09-17: X1002Q1과 동일한 apiX1002Q1G1(ZA_NOTICE)로 교체 - 이미 important_cd DESC, NOTICE_ID DESC
  // 순으로 정렬돼 오므로 앞에서 5건만 잘라 쓰면 됨.
  // 읽음상태(read_yn)는 ZA_NOTICE_READ 테이블(USER_ID+NOTICE_ID)에 실제로 영구 저장되어, GRID1이
  // @USER_ID 기준으로 LEFT JOIN해서 내려주는 값을 그대로 신뢰함(새로고침해도 유지) - 더 이상
  // 클라이언트 상태로만 흉내내지 않음.
  // 아이콘은 개봉(읽음)/미개봉(안읽음) 2종만 사용 - fa-envelope(미개봉)/fa-envelope-open(개봉).
  interface NoticeItem { id: string; readYn: 'Y' | 'N'; title: string; message: string; regDt: string; expireDt: string; isImportant: boolean }
  const [hasNewNotice, setHasNewNotice] = useState(false);
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  // 2026-09-19: SP는 important_cd(중요도)만 보고 상단고정 순서를 매기는데, 게시기간(EXPIRE_DT)이
  // 지난 "중요" 공지까지 계속 상단에 남는 건 이 팝업(최근 공지 미리보기) 취지에 안 맞음(만료된 건
  // 더 이상 안내할 필요가 없으므로) - 만료 여부는 이 팝업에서만 재계산해서 일반 공지와 동일하게
  // 취급함(X1002Q1 전체 목록화면은 원래 SP 정렬을 그대로 써서 손대지 않음).
  const isImportantHigh = (v?: string) => v === 'A' || v === 'Y';
  const isExpired = (expireDt?: string) => {
    if (!expireDt) return false;
    const dateOnly = expireDt.slice(0, 10); // 'YYYY-MM-DD'
    if (!dateOnly || dateOnly.startsWith('1900')) return false; // 미설정(구자료 기본값)은 무기한 취급
    // toISOString()은 UTC 기준이라 자정 근처(예: 새벽 0~9시, KST 기준 UTC+9)에는 하루 전 날짜로
    // 잘못 비교될 수 있어 로컬 날짜로 직접 조합함.
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return dateOnly < todayStr;
  };
  // 팝업에 표시할 만료일자 - 미설정(빈값/구자료 기본값 1900-01-01, 무기한 게시)은 표시하지 않음
  const formatExpireDt = (expireDt?: string) => {
    const dateOnly = (expireDt || '').slice(0, 10);
    return (!dateOnly || dateOnly.startsWith('1900')) ? '' : dateOnly;
  };
  const checkNewNotices = useCallback(async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/za/apiX1002Q1G1`, { data: { keyword_h: '', user_id: getUserInfo()?.userId || '' } });
      const rows = Array.isArray(res.data?.rsResults) ? res.data.rsResults : [];
      // 만료되지 않은 중요공지만 상단고정(pin=0), 나머지(일반 + 만료된 중요공지)는 등록일 최신순으로
      // 재정렬(안정정렬이 아니라 reg_dt를 직접 비교해서, SP가 애초에 중요도로 먼저 묶어놓은 순서에
      // 영향받지 않도록 함).
      const sortedRows = [...rows].sort((a: any, b: any) => {
        const aPin = isImportantHigh(a.important_cd) && !isExpired(a.expire_dt) ? 0 : 1;
        const bPin = isImportantHigh(b.important_cd) && !isExpired(b.expire_dt) ? 0 : 1;
        if (aPin !== bPin) return aPin - bPin;
        return (b.reg_dt || '').localeCompare(a.reg_dt || '');
      });
      // 2026-09-18: X1001M1(공지사항등록)이 SunEditor로 HTML을 저장하게 되면서, 이 드롭다운 미리보기는
      // (dangerouslySetInnerHTML 없이 일반 텍스트로만 렌더링하므로) 태그를 그대로 지우고 보여줘야 함.
      const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const mapped: NoticeItem[] = sortedRows.slice(0, 5).map((row: any) => ({
        id: String(row.notice_id),
        readYn: row.read_yn === 'Y' ? 'Y' : 'N',
        title: row.title,
        message: stripHtml(row.content || ''),
        regDt: row.reg_dt,
        expireDt: formatExpireDt(row.expire_dt),
        // 상단고정 여부와 동일한 기준(중요 & 미만료)으로 굵게/연하게를 결정 - 만료된 중요공지는
        // 정렬뿐 아니라 글자 굵기도 일반 공지와 똑같이 보이도록 함.
        isImportant: isImportantHigh(row.important_cd) && !isExpired(row.expire_dt),
      }));
      setNotices(mapped);
      setHasNewNotice(mapped.some(n => n.readYn === 'N'));
    } catch {
      setNotices([]);
      setHasNewNotice(false);
    }
  }, []);
  useEffect(() => {
    checkNewNotices();
    const timer = setInterval(checkNewNotices, 5 * 60 * 1000); // 5분마다 주기적 체크
    return () => clearInterval(timer);
  }, [checkNewNotices]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 공지사항 팝업(로그인 직후 폼로드 시 자동 노출) - X1001M1(공지사항등록)에서 "팝업여부"를 켜둔 활성
  // 공지를 로그인해서 mainFrame이 처음 뜰 때 모달로 보여줌. 여러 건이면 한 모달 안에 목록형으로 전부
  // 표시함. 각 항목에는 "오늘 하루 다시 보지 않기" 체크박스가 있어, 체크한 뒤 닫으면 그 공지는 오늘
  // 날짜가 지나기 전까지 이 브라우저에서는 다시 안 뜸(localStorage에 날짜와 함께 기록, 서버 상태는
  // 아님 - 다른 기기/브라우저에서는 별개로 다시 뜸).
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  interface PopupNoticeItem { id: string; title: string; content: string; regDt: string; files?: any[] }
  const [popupNotices, setPopupNotices] = useState<PopupNoticeItem[]>([]);
  const [showNoticePopup, setShowNoticePopup] = useState(false);
  const [popupDismissToday, setPopupDismissToday] = useState<Record<string, boolean>>({});

  // 홈(HomePageU1) 알림 센터 위젯의 공지 클릭 - 공지사항조회 탭으로 이동하지 않고 간단한 미리보기 팝업만
  // 띄움(2026-10-03). 더 보고 싶으면(첨부파일 등) 팝업 안의 "자세히보기"에서 그때 탭을 엶.
  interface QuickNoticeItem { id: string; title: string; content: string; regDt: string; scopeTp?: string; expireDt?: string }
  const [quickNotice, setQuickNotice] = useState<QuickNoticeItem | null>(null);
  const [quickNoticeFiles, setQuickNoticeFiles] = useState<any[]>([]); // 첨부파일(X1001M1에서 등록, 다운로드만 가능) - X1002Q1의 detailFiles와 동일 패턴
  const POPUP_DISMISS_KEY = 'noticePopupDismissedUntil'; // { [notice_id]: 'YYYY-MM-DD'(오늘 하루 안보기를 누른 날) }

  const todayStr = useCallback(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/za/apiX1002Q1Popup`, { data: {} });
        const rows = Array.isArray(res.data?.rsResults) ? res.data.rsResults : [];
        if (rows.length === 0) return;

        let dismissedMap: Record<string, string> = {};
        try { dismissedMap = JSON.parse(localStorage.getItem(POPUP_DISMISS_KEY) || '{}'); } catch { dismissedMap = {}; }
        const today = todayStr();
        const targets = rows.filter((row: any) => dismissedMap[row.notice_id] !== today);
        if (targets.length === 0) return;

        // 첨부파일(X1001M1에서 등록) - 조회전용이라 실패해도 팝업 자체는 그대로 떠야 하므로 건별로 개별 catch
        const filesList = await Promise.all(targets.map((row: any) =>
          axios.post(`${API_BASE_URL}/za/apiX1002Q1FileList`, { data: { notice_id: row.notice_id } })
            .then(r => Array.isArray(r.data?.rsResults) ? r.data.rsResults : [])
            .catch(() => [])
        ));

        setPopupNotices(targets.map((row: any, i: number) => ({
          id: String(row.notice_id),
          title: row.title,
          content: row.content || '',
          regDt: row.reg_dt,
          files: filesList[i],
        })));
        setShowNoticePopup(true);
      } catch {
        // 팝업조회 실패는 로그인/화면진입 자체를 막을 이유가 없으므로 조용히 무시함
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNoticePopupClose = useCallback(() => {
    const today = todayStr();
    const checkedIds = Object.keys(popupDismissToday).filter(id => popupDismissToday[id]);
    if (checkedIds.length > 0) {
      let dismissedMap: Record<string, string> = {};
      try { dismissedMap = JSON.parse(localStorage.getItem(POPUP_DISMISS_KEY) || '{}'); } catch { dismissedMap = {}; }
      checkedIds.forEach(id => { dismissedMap[id] = today; });
      try { localStorage.setItem(POPUP_DISMISS_KEY, JSON.stringify(dismissedMap)); } catch { /* localStorage 사용불가 시 그냥 무시 */ }
    }
    setShowNoticePopup(false);
    setPopupDismissToday({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupDismissToday]);

  // 모두 읽음 - 배지를 끄고 목록의 모든 항목을 개봉 상태로 전환 + 서버(ZA_NOTICE_READ)에 실제 저장
  const handleNoticeMarkAllRead = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setHasNewNotice(false);
    setNotices(prev => prev.map(n => ({ ...n, readYn: 'Y' })));
    axios.post(`${API_BASE_URL}/za/apiX1002Q1ReadAll`, { data: { user_id: getUserInfo()?.userId || '' } }).catch(() => {});
  }, []);

  // 메뉴 표시방식(사이드바/상단메가메뉴) 토글 - 헤더의 토글 버튼에서 호출
  const handleMenuStyleToggle = () => {
    const nextStyle: MenuStyle = menuStyle === 'topmenu' ? 'sidebar' : 'topmenu';
    setMenuStyle(nextStyle);
    setStoredMenuStyle(nextStyle);
    setShowMegaMenu(false);
    setShowSubSidebar(false);
  };

  // 2026-09-20: 좌측 메인사이드바의 [홈] 자리를 상단메가메뉴 전환 버튼으로 사용 - 단순 모드전환(handleMenuStyleToggle)과
  // 달리, 전환과 동시에 메가메뉴 패널까지 바로 열어줘야 "클릭하면 메가메뉴로 전환"이라는 요청에 맞음.
  // 홈 기능 자체는 삭제하지 않음 - 메가메뉴 퀵메뉴 안의 [홈] 버튼으로 계속 접근 가능.
  const handleSwitchToMegaMenu = () => {
    setMenuStyle('topmenu');
    setStoredMenuStyle('topmenu');
    setShowSubSidebar(false);
    setShowMegaMenu(true);
  };

  const [mainMenuData, setMainMenuData] = useState<MainMenuItem[]>([]);
  // ZA_MENU(LINK_KEY='HOME').CALL_PGM_ID(모든 사용자 기본 홈) - null: 메뉴 조회 전(로딩), '': HOME 메뉴 없음/호출프로그램ID 비어있음
  const [menuHomePgmId, setMenuHomePgmId] = useState<string | null>(null);
  // ZA_USER_HOME(Z3901M1 홈설정 탭)에서 이 사용자에게 지정한 홈의 CALL_PGM_ID - null: 조회 전(로딩), '': 지정 없음(또는 조회 실패)
  const [userHomePgmId, setUserHomePgmId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    axios.post(`${API_BASE_URL}/home/apiHomeUserPgm`, {})
      .then(res => { if (!cancelled) setUserHomePgmId(String(res.data?.rsResults?.call_pgm_id || '').trim()); })
      .catch(() => { if (!cancelled) setUserHomePgmId(''); }); // 조회 실패 시 지정 없음으로 보고 ZA_MENU 기본 홈 사용
    return () => { cancelled = true; };
  }, []);
  // 실제로 보여줄 홈: 사용자별 지정(ZA_USER_HOME)이 있으면 그것(메뉴 조회를 기다리지 않음), 없으면 ZA_MENU의 HOME 기본값(메뉴 조회 후).
  //  - 조회 전에는 이 브라우저에서 마지막으로 확정된 홈 ID(localStorage 캐시)로 먼저 화면을 띄워 새로고침/재로그인 때 "로딩 중" 깜빡임을 없앰.
  //    캐시가 없으면(최초) null(로딩). 조회 결과가 캐시와 다르면 결과대로 바뀜.
  const homeCacheKey = `app_home_pgm:${getUserInfo()?.userId || ''}`;
  const cachedHomePgmId: string | null = (() => { try { return localStorage.getItem(homeCacheKey) || null; } catch { return null; } })();
  const resolvedHomePgmId: string | null = userHomePgmId
    ? userHomePgmId
    : (userHomePgmId === '' && menuHomePgmId !== null ? menuHomePgmId : null);
  const homePgmId: string | null = resolvedHomePgmId ?? cachedHomePgmId;
  useEffect(() => {
    if (resolvedHomePgmId === null) return;
    try { if (resolvedHomePgmId) localStorage.setItem(homeCacheKey, resolvedHomePgmId); else localStorage.removeItem(homeCacheKey); } catch { /* 캐시 실패는 무시 */ }
  }, [resolvedHomePgmId, homeCacheKey]);
  const [subMenuData, setSubMenuData] = useState<Record<string, SubMenuData>>({});
  const [activeMainMenu, setActiveMainMenu] = useState<string>('');
  const [showSubSidebar, setShowSubSidebar] = useState(false);
  // 핀 고정(기본) - 고정이면 서브사이드바가 메뉴 클릭 후에도 열려 있고 닫기(≡) 아이콘을 숨김. 미고정이면
  // 화면(리프) 메뉴를 클릭할 때 서브사이드바가 자동으로 닫히고 닫기 아이콘을 보임(사용자별 브라우저 저장)
  const SUB_SIDEBAR_PINNED_KEY = 'mainFrame:subSidebarPinned';
  const [subSidebarPinned, setSubSidebarPinned] = useState<boolean>(() => {
    try { return localStorage.getItem(SUB_SIDEBAR_PINNED_KEY) !== 'N'; } catch { return true; }
  });
  const toggleSubSidebarPinned = () => {
    const next = !subSidebarPinned;
    setSubSidebarPinned(next);
    try { localStorage.setItem(SUB_SIDEBAR_PINNED_KEY, next ? 'Y' : 'N'); } catch { /* 저장 실패는 무시(이번 세션에서만 유지) */ }
  };
  // 서브사이드바 폭(px) - 사용자가 sub-sidebar-resizer를 드래그해서 조절, 브라우저에 사용자별로 저장.
  // 그리드 분할(SplitContainer)과 달리 퍼센트가 아니라 고정 px로 관리함 - 메뉴트리 사이드바는 화면이
  // 넓어져도 사용자가 정한 폭 그대로 유지되는 게 맞고, 퍼센트로 하면 큰 모니터에서 불필요하게 넓어짐.
  const SUB_SIDEBAR_WIDTH_KEY = 'mainFrame:subSidebarWidth';
  const SUB_SIDEBAR_MIN_WIDTH = 220;
  const SUB_SIDEBAR_MAX_WIDTH = 500;
  const [subSidebarWidth, setSubSidebarWidth] = useState<number>(() => {
    try {
      const stored = Number(localStorage.getItem(SUB_SIDEBAR_WIDTH_KEY));
      if (Number.isFinite(stored) && stored >= SUB_SIDEBAR_MIN_WIDTH && stored <= SUB_SIDEBAR_MAX_WIDTH) return stored;
    } catch { /* 저장소 접근 실패 시 기본값 사용 */ }
    return 340; // mainFrameV1.css --sub-sidebar-width 기본값과 동일
  });
  const [isSubSidebarDragging, setIsSubSidebarDragging] = useState(false); // 드래그 중엔 width 트랜지션을 꺼서 끌림 방지
  const isSidebarDraggingRef = useRef(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [expandedSubMenus, setExpandedSubMenus] = useState<Set<string>>(new Set());
  const [pendingScrollMenuKey, setPendingScrollMenuKey] = useState<string | null>(null); // 탭 아이콘 클릭 시 사이드메뉴에서 스크롤로 찾아갈 항목
  const [userInfo, setUserInfo] = useState<ifUserInfo | null>(null);
  // 사용자유형(ZA_USER.USER_KIND, 공통코드 Z0007) 명칭 - 사용자 드롭다운의 이름 우측 표기 / 아이콘 하단 배지(명칭의 첫 글자: 관리자=관, 플래너=플, 사용자=사)
  const { options: cbUserKind } = useComboBox('Z0007', false);
  // user_kind가 없으면(빈 값) 회사사용자(CU)로 표기 - 아직 조회 전(undefined)일 때는 표시하지 않음(잘못된 값이 잠깐 보이는 것 방지)
  const userKindCd = userInfo?.userKind === undefined ? '' : (userInfo.userKind || 'CU');
  const userKindNm = cbUserKind.find(o => o.value === userKindCd)?.label || '';
  const [languages, getLanguages] = useState<Array<{ code: string; label: string; flag?: string }>>([]);
  const [solutionTitle, setSolutionTitle] = useState('');   // 좌측상단 로고 옆 브랜드 텍스트 - apiMainMenuTree 응답의 .env(SOLUTION_TITLE) 값
  const brandTextRef = useRef<HTMLSpanElement>(null);       // .env 값 길이가 제각각이라 CSS max-width만으론 잘려버려서, 넘치면 폰트크기를 자동으로 줄임(아래 effect)
  const { ddKey, language, getLanguage, setDDData } = useCommonTranslation();
  // 메세지박스 모달
  const [chipMsg, setChipMsg] = useState<{ label: string; color?: any; visible: boolean; location?: Record<string, number> }>({ label: '', color: 'info', visible: false, location: { top: 350, right: 24 } }); // chipMsg 상태
  const { showMsgBox, confirmMsgBox } = useMsgBoxModal(); // 메시지박스 모달 / 메시지박스(확인) 모달
  // 팝업 모달 (공통팝업 : showCommonPopup, 사용자팝업 : showUserPopup)
  const { showCommonPopup} = useCommonPopupModal(); // 공통팝업 모달
  const { showUserPopup} = useUserPopupModal(); // 사용자팝업 모달
  const { showRefPopup} = useRefPopupModal(); // 레퍼런스 팝업 모달

  // 탭(openTabs) 상태 및 탭 생명주기 관리
  const {
    openTabs, activateTab, addTab, closeTab, closeAllTabs, closeOtherTabs,
    closeCurrentTab, refreshAllTabs, closeLeftTabs, closeRightTabs, moveTab, refreshTabTitles, setTabTitle,
  } = useTabs({ ddKey, language, confirmMsgBox,
    closeDropdowns: () => { setShowCtrlTabsDropdown(false); setShowUserDropdown(false); setShowNoticeDropdown(false); setTabCtxMenu(null); },
  });

  // 2026-09-20: 공지사항 탭을 열 때 쓰는 메뉴 정보(제목/아이콘/URL)를 하드코딩하지 않고 사이드메뉴
  // (subMenuData - ZA_MENU에서 내려온 실제 등록 메뉴)에서 찾아서 씀. 메뉴관리 화면에서 이름/URL을 바꾸면
  // 여기 3곳(공지사항 목록 클릭/메가메뉴 퀵메뉴/알림드롭다운 제목링크)도 자동으로 같이 반영됨.
  // ZA_MENU.LINK_KEY='NOTICE'인 메뉴를 찾음(URL/PGM_ID 문자열에 의존하지 않음 - 화면 프로그램ID가 바뀌어도
  // LINK_KEY만 유지하면 계속 찾아짐). 1depth items와 2depth children까지 재귀적으로 확인.
  // groupKey(subMenuData의 키 = 그 메뉴가 속한 사이드 메인메뉴)도 같이 반환 - addTab에 parentMenuKey로
  // 넘겨줘야 나중에 탭 아이콘 클릭(handleTabIconClick)이 이 탭의 서브메뉴를 찾아 토글할 수 있음.
  // 2026-09-20: 1:1상담톡(LINK_KEY='CHAT') 바로가기(플로팅 버튼)도 같은 방식으로 찾으므로 linkKey를
  // 인자로 받는 범용 탐색 함수로 일반화하고, 공지사항은 이 함수의 'NOTICE' 호출로 유지함.
  // 탭 위치 표시줄용 메뉴 경로 - 메인메뉴 > (그룹 >) 화면. 메인메뉴 이름은 ZA_MENU_LANG(메뉴 조회 응답)의 title을 사용.
  const menuPathFor = useCallback((mainKey: string | undefined, itemKey: string): string | undefined => {
    if (!mainKey) return undefined;
    const mainTitle = ddKey(mainKey) || mainMenuData.find(m => m.key === mainKey)?.title || mainKey;
    const nameOf = (it: SubMenuItem) => ddKey(it.itemKey) || it.itemName || it.itemKey;
    return buildMenuPath(mainTitle, subMenuData[mainKey]?.items || [], itemKey, nameOf) || undefined;
  }, [mainMenuData, subMenuData, ddKey]);

  // itemKey까지 펼쳐야 할 조상 그룹(children 있는 항목)의 itemKey 목록 - 못 찾으면 null, 최상위(펼칠 조상 없음)면 []
  const findMenuAncestorPath = (items: SubMenuItem[], targetKey: string): string[] | null => {
    for (const it of items) {
      if (it.itemKey === targetKey) return [];
      if (Array.isArray(it.children) && it.children.length > 0) {
        const sub = findMenuAncestorPath(it.children, targetKey);
        if (sub) return [it.itemKey, ...sub];
      }
    }
    return null;
  };
  const findMenuItemByLinkKey = useCallback((linkKey: string): { item: SubMenuItem; groupKey: string } | undefined => {
    for (const [groupKey, group] of Object.entries(subMenuData)) {
      for (const item of group.items) {
        if (item.linkKey === linkKey) return { item, groupKey };
        const child = item.children?.find(c => c.linkKey === linkKey);
        if (child) return { item: child, groupKey };
      }
    }
    // 하위메뉴 없이 화면을 바로 여는 단독 메인메뉴(hasSubMenu:false + url)도 같은 LINK_KEY로 찾음 - 이 경우 속한 그룹이 없어 groupKey는 ''
    const leaf = mainMenuData.find(m => !m.hasSubMenu && m.url && m.linkKey === linkKey);
    if (leaf) {
      return { item: { itemKey: leaf.key, itemName: leaf.title, icon: leaf.icon, url: leaf.url, paramData: leaf.paramData, linkKey: leaf.linkKey } as SubMenuItem, groupKey: '' };
    }
    return undefined;
  }, [subMenuData, mainMenuData]);
  const findNoticeMenuItem = useCallback(() => findMenuItemByLinkKey('NOTICE'), [findMenuItemByLinkKey]);

  // 2026-09-23: 외부URL 메뉴 - ZA_MENU.CALL_PGM_ID에 내부 화면ID('모듈/화면ID') 대신 완전한 URL을 넣어두면,
  // 클릭 시 xGA 내부 탭을 열지 않고 새 브라우저 탭으로 그 URL을 그대로 열어줌.
  // 2026-09-30: LINK_KEY='EXTURL' 명시가 없어도 CALL_PGM_ID가 http(s):// 로 시작하면 자동으로 새 브라우저로 열리게 함
  // (메뉴 등록 시 URL마다 EXTURL을 따로 챙겨 넣을 필요 없음). LINK_KEY='ADDTAB'이면 예외적으로 내부 탭 안 iframe에 표시.
  // CALL_PGM_ID는 nvarchar(50)이라 URL이 50자를 넘으면 등록 시 잘리므로 주의.
  const isHttpUrl = (url?: string) => !!url && /^https?:\/\//i.test(url);
  const isExternalUrlLink = (url?: string, linkKey?: string) => linkKey === 'EXTURL' || (isHttpUrl(url) && linkKey !== 'ADDTAB');
  const openExternalUrlTab = (url: string) => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); };

  // 2026-09-20: 플로팅 상담버튼(ChatWidget) - 사용자 메뉴에 1:1상담톡(LINK_KEY='CHAT')이 있을 때만 노출하고,
  // 담당자 클릭/이미 열린 탭 이동 시 그 메뉴 정보로 탭을 열거나 활성화함.
  const chatMenu = useMemo(() => findMenuItemByLinkKey('CHAT'), [findMenuItemByLinkKey]);
  const openChatTab = useCallback(() => {
    if (!chatMenu) return;
    const itemKey = chatMenu.item.itemKey || '';
    addTab({
      id: itemKey,
      title: ddKey(itemKey) || chatMenu.item.itemName || '1:1상담톡',
      url: chatMenu.item.url || '',
      originalKey: itemKey,
      icon: chatMenu.item.icon || 'fas fa-comments',
      parentMenuKey: chatMenu.groupKey,
      menuPath: menuPathFor(chatMenu.groupKey, itemKey),
    });
  }, [chatMenu, addTab, ddKey, menuPathFor]);

  // 2026-09-20: 일정관리 알림(ScheduleAlert) - 사용자 메뉴에 일정관리(LINK_KEY='SCHEDULE')가 있을 때만 동작하고,
  // 알림의 "일정관리 열기"는 그 메뉴 정보로 탭을 열거나 활성화함.
  const scheduleMenu = useMemo(() => findMenuItemByLinkKey('SCHEDULE'), [findMenuItemByLinkKey]);
  const noticeMenu = useMemo(() => findMenuItemByLinkKey('NOTICE'), [findMenuItemByLinkKey]);   // 공지사항 메뉴(LINK_KEY='NOTICE') - 사용자 메뉴에 있을 때만 메가메뉴 바로가기에 노출
  const openScheduleTab = useCallback((extra?: { paramData?: Record<string, any>; autoQuery?: boolean; activate?: boolean }) => {
    if (!scheduleMenu) return;
    const itemKey = scheduleMenu.item.itemKey || '';
    addTab({
      id: itemKey,
      title: ddKey(itemKey) || scheduleMenu.item.itemName || '일정관리',
      url: scheduleMenu.item.url || '',
      originalKey: itemKey,
      icon: scheduleMenu.item.icon || 'fas fa-calendar-alt',
      parentMenuKey: scheduleMenu.groupKey,
      menuPath: menuPathFor(scheduleMenu.groupKey, itemKey),
      ...extra,
    });
  }, [scheduleMenu, addTab, ddKey, menuPathFor]);
  // HomePage 일정카드 -> 일정 확인 팝업만 띄움(탭 전환 없음, 2026-10-03) - 일정관리 탭은 백그라운드로
  // 마운트/보장만 해두고(activate:false) 화면은 계속 홈에 머무름. 탭이 아직 없으면 paramData(JumpIn)로
  // 그 화면이 마운트되면서 스스로 다이얼로그를 열고, 이미 열려있으면 addTab이 아무것도 안 하므로(activate:false라
  // 활성화도 안 함) window.gotoSchedule로 직접 지시함 - 둘 다 Dialog(MUI Portal)라 탭이 비활성이어도 그대로 뜸.
  const handleOpenScheduleFromHome = useCallback((opt?: { date?: string; sch_no?: string; isNew?: boolean }) => {
    openScheduleTab({ paramData: opt || {}, autoQuery: true, activate: false });
    if (opt) window.gotoSchedule?.(opt);
  }, [openScheduleTab]);
  // 그 팝업(일정 확인/등록) 안의 "일정관리로 이동" 아이콘 - 팝업은 그대로 두고 탭만 활성화(화면 전환)
  useEffect(() => {
    window.activateScheduleTab = () => openScheduleTab();
    return () => { window.activateScheduleTab = undefined; };
  }, [openScheduleTab]);

  // 공지사항 탭 열기(activate) - 사이드메뉴에서 찾은 메뉴 정보를 쓰되, 아직 메뉴데이터가 로드되기 전이거나
  // 메뉴가 등록되어 있지 않은 예외적인 경우를 대비해 기존 하드코딩 값을 폴백으로 유지함.
  const openNoticeTab = useCallback((extra?: { paramData?: Record<string, any>; autoQuery?: boolean }) => {
    const found = findNoticeMenuItem();
    const menuItem = found?.item;
    const itemKey = menuItem?.itemKey || '';
    addTab({
      id: itemKey,
      title: ddKey(itemKey) || menuItem?.itemName || '공지사항조회',
      url: menuItem?.url || '',
      originalKey: itemKey,
      icon: menuItem?.icon || 'fas fa-bullhorn',
      // 2026-09-20: handleSubMenuClick과 동일하게 parentMenuKey를 넘겨야 탭 아이콘 클릭 시 사이드
      // 서브메뉴 토글(handleTabIconClick)이 정상 동작함(이게 빠져서 메가메뉴/알림팝업으로 연 탭만
      // 아이콘 토글이 안 먹히던 원인).
      parentMenuKey: found?.groupKey,
      menuPath: menuPathFor(found?.groupKey, itemKey),
      ...extra,
    });
    // 2026-09-20: 특정 공지 상세로 가는 게 아니라(=extra.paramData.notice_id 없음) 그냥 "공지사항조회"
    // 목록을 열려는 호출이면, 이미 열려있는 탭이 상세화면을 보고 있던 경우까지 대비해 명시적으로 목록
    // 복귀를 지시함(addTab은 이미 열린 탭을 활성화만 할 뿐 내부 viewMode는 안 건드리므로 이게 없으면
    // 상세를 먼저 본 뒤 "공지사항조회"를 눌러도 계속 상세화면에 머무는 문제가 있었음).
    if (!extra?.paramData?.notice_id) {
      window.gotoNoticeList?.();
    }
  }, [findNoticeMenuItem, addTab, ddKey, menuPathFor]);

  // 홈(HomePageV1) "공지사항" 위젯의 [+] - 공지사항등록(X1001M1, LINK_KEY='NOTICE_NEW') 탭을 새 글쓰기
  // 상태로 염(사용자 메뉴에 없으면 아무 것도 하지 않음 - 공지 작성 권한이 없는 사용자는 메뉴 자체가 없음).
  const noticeNewMenu = useMemo(() => findMenuItemByLinkKey('NOTICE_NEW'), [findMenuItemByLinkKey]);
  const handleNewNoticeFromHome = useCallback(() => {
    if (!noticeNewMenu) return;
    const itemKey = noticeNewMenu.item.itemKey || '';
    addTab({
      id: itemKey,
      title: ddKey(itemKey) || noticeNewMenu.item.itemName || '공지사항등록',
      url: noticeNewMenu.item.url || '',
      originalKey: itemKey,
      icon: noticeNewMenu.item.icon || 'fas fa-bullhorn',
      parentMenuKey: noticeNewMenu.groupKey,
      menuPath: menuPathFor(noticeNewMenu.groupKey, itemKey),
      paramData: { isNew: true },
    });
  }, [noticeNewMenu, addTab, ddKey, menuPathFor]);

  // 공지사항 행 클릭 -> 해당 항목을 개봉 상태로 전환하고, X1002Q1(공지사항조회) 탭에서 상세를 보여줌.
  // 새 탭(X1002Q2)을 열지 않고 X1002Q1 탭을 열거나 활성화한 뒤, 그 화면이 자체적으로 상세보기로
  // 전환하도록 함:
  //  - X1002Q1 탭이 아직 없으면 addTab의 paramData.notice_id를 통해 최초 마운트 시 상세로 진입(JumpIn).
  //  - 이미 열려있으면 addTab은 활성화만 하고 paramData를 재전달하지 않으므로, 이미 마운트돼 있는
  //    X1002Q1이 노출해둔 window.gotoNoticeDetail을 직접 호출해서 상세로 전환시킴.
  //  읽음처리(ZA_NOTICE_READ 저장)는 X1002Q1의 fncShowDetail이 두 경로 모두에서 공통 수행함.
  const handleNoticeItemClick = useCallback((notice: NoticeItem) => {
    setShowNoticeDropdown(false);
    setNotices(prev => prev.map(n => n.id === notice.id ? { ...n, readYn: 'Y' } : n));
    openNoticeTab({ paramData: { notice_id: notice.id }, autoQuery: true });
    window.gotoNoticeDetail?.(notice.id);
  }, [openNoticeTab]);

  // 홈(HomePageU1) 알림 센터의 공지 -> noticeId가 있으면 그 공지 상세(위 공지사항 알림표시 항목 클릭과 동일), 없으면 공지사항조회 목록
  const handleOpenNoticeFromHome = useCallback((noticeId?: string) => {
    if (!noticeId) { openNoticeTab(); return; }
    setNotices(prev => prev.map(n => n.id === noticeId ? { ...n, readYn: 'Y' } : n));
    openNoticeTab({ paramData: { notice_id: noticeId }, autoQuery: true });
    window.gotoNoticeDetail?.(noticeId);
  }, [openNoticeTab]);
  useEffect(() => {   // 다른 화면에서 공지 상세로 보내달라고 요청할 수 있게 노출(일정관리의 공지 만료일 [상세보기] 등)
    window.openNoticeDetail = handleOpenNoticeFromHome;
    return () => { window.openNoticeDetail = undefined; };
  }, [handleOpenNoticeFromHome]);

  // 홈 알림 센터 위젯 전용 - noticeId가 있으면 탭 이동 없이 미리보기 팝업(quickNotice)만 채움, 없으면("공지사항
  // 전체보기") 기존처럼 목록 탭을 염. handleOpenNoticeFromHome(위, window.openNoticeDetail 전역으로도 쓰임)은
  // 다른 화면(일정관리 등)이 기대하는 "탭 이동" 동작을 그대로 유지해야 해서 그대로 두고, 홈 위젯만 이걸로 분리함.
  const handleQuickNoticeFromHome = useCallback((noticeId?: string) => {
    if (!noticeId) { openNoticeTab(); return; }
    setNotices(prev => prev.map(n => n.id === noticeId ? { ...n, readYn: 'Y' } : n));
    setQuickNoticeFiles([]);
    axios.post(`${API_BASE_URL}/za/apiX1002Q1Detail`, { data: { notice_id: noticeId } })
      .then(res => {
        const d = res.data?.rsResults;
        setQuickNotice(d ? { id: noticeId, title: d.title || '', content: d.content || '', regDt: d.reg_dt || '', scopeTp: d.scope_tp || '', expireDt: d.expire_dt || '' } : null);
      })
      .catch(() => setQuickNotice(null));
    // 첨부파일(X1001M1에서 등록) - 조회전용이라 실패해도 팝업 내용 자체는 그대로 보여야 하므로 별도 처리(X1002Q1 detailFiles와 동일 패턴)
    axios.post(`${API_BASE_URL}/za/apiX1002Q1FileList`, { data: { notice_id: noticeId } })
      .then(res => setQuickNoticeFiles(Array.isArray(res.data?.rsResults) ? res.data.rsResults : []))
      .catch(() => setQuickNoticeFiles([]));
    axios.post(`${API_BASE_URL}/za/apiX1002Q1ReadOne`, { data: { user_id: getUserInfo()?.userId || '', notice_id: noticeId } })
      .then(() => window.refreshNoticeAlerts?.())
      .catch(() => {});
  }, [openNoticeTab]);
  const handleQuickNoticeClose = useCallback(() => { setQuickNotice(null); setQuickNoticeFiles([]); }, []);
  // 팝업 머리의 "자세히보기" 아이콘 - 기존처럼 공지사항조회 탭으로 실제 이동(첨부파일 등 전체 내용 확인용)
  const handleQuickNoticeMore = useCallback(() => {
    if (!quickNotice) return;
    const id = quickNotice.id;
    setQuickNotice(null);
    handleOpenNoticeFromHome(id);
  }, [quickNotice, handleOpenNoticeFromHome]);

  // 활성 탭 우클릭 -> 탭관리 메뉴를 클릭 좌표에 표시. 탭 위에서는 브라우저 기본 우클릭 메뉴를 항상 막고,
  // 메뉴 자체는 "활성 탭이면서 Home이 아닌" 경우에만 띄움(비활성 탭/Home 우클릭은 아무 것도 안 뜸).
  const handleTabContextMenu = (e: React.MouseEvent, tab: TabItem) => {
    e.preventDefault();
    if (!tab.active || tab.id === 'Home') return;
    setShowUserDropdown(false);
    setShowCtrlTabsDropdown(false);
    setShowNoticeDropdown(false);
    setTabCtxMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
  };
  const handleCloseTabCtxMenu = () => setTabCtxMenu(null);

  // 탭 아이콘 클릭 - 이미 활성화된 탭이면 그 탭이 속한 메뉴의 사이드(서브)메뉴를 토글, 비활성 탭이면 그냥 탭 전환
  //  (탭 본문 클릭과 분리 - 아이콘 클릭만으로 토글되도록 stopPropagation 처리해서 버튼 onClick으로 전파 안 함)
  const handleTabIconClick = (e: React.MouseEvent, tab: TabItem) => {
    e.stopPropagation();
    if (!tab.active) {
      activateTab(tab.id);
      return;
    }
    // 메가메뉴(topmenu) 상태로 최초 로그인하면 좌측 사이드바를 한 번도 클릭한 적이 없어 activeMainMenu가
    // 계속 빈 값이라, [홈] 탭 아이콘을 눌러도 "어느 메뉴의 서브메뉴를 보여줘야 할지" 알 수 없어 토글이
    // 먹통이었음 - activeMainMenu가 없으면 사이드 메인메뉴 중 서브메뉴를 가진 첫 번째 항목으로 대신함.
    const menuKey = tab.id === 'Home'
      ? (activeMainMenu || mainMenuData.find(m => m.key !== 'Home' && subMenuData[m.key])?.key || '')
      : tab.parentMenuKey;
    if (!menuKey || !subMenuData[menuKey]) return;
    // 2026-09-28: 이미 열려있으면 닫는 토글 동작은 잠시 비활성화 - 항상 펼쳐서 위치를 찾아가기만 함
    // if (showSubSidebar && activeMainMenu === menuKey) {
    //   setShowSubSidebar(false);
    // } else {
      setActiveMainMenu(menuKey);
      setShowSubSidebar(true);
      // 이 탭의 메뉴가 속한 조상 그룹까지 모두 펼치고, 렌더링 후 그 위치로 스크롤 이동
      const targetKey = tab.originalKey || tab.id;
      const ancestorPath = findMenuAncestorPath(subMenuData[menuKey].items, targetKey);
      if (ancestorPath) {
        setExpandedSubMenus(new Set(ancestorPath));
        setPendingScrollMenuKey(targetKey);
      }
    // }
  };
  // handleTabIconClick이 예약한 스크롤 이동 - 사이드메뉴가 열리고 조상 그룹이 펼쳐져 DOM에 나타난 뒤 실행
  useEffect(() => {
    if (!pendingScrollMenuKey || !showSubSidebar) return;
    const el = document.getElementById(`sideMenuItem-${pendingScrollMenuKey}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingScrollMenuKey(null);
    }
  }, [pendingScrollMenuKey, showSubSidebar, activeMainMenu, expandedSubMenus]);
  // 모바일 서브사이드바 토글 - 열려있으면 닫고, 닫혀있으면 보던 메인메뉴(없으면 서브메뉴를 가진 첫 메인메뉴)의 서브메뉴를 엶
  const handleMobileSubSidebarToggle = () => {
    if (showSubSidebar) {
      setShowSubSidebar(false);
      return;
    }
    const menuKey = (activeMainMenu && subMenuData[activeMainMenu])
      ? activeMainMenu
      : (mainMenuData.find(m => m.key !== 'Home' && subMenuData[m.key])?.key || '');
    // 사용 가능한 메뉴가 하나도 없어도(권한 미부여 등) 토글은 동작해서 빈 서브사이드바(안내문구)를 열고 닫음
    if (menuKey) setActiveMainMenu(menuKey);
    setShowSubSidebar(true);
  };
  const tabCtxMenuRef = useRef<HTMLDivElement>(null); // 우클릭 메뉴 영역(바깥 클릭 감지용)
  useClickOutside(tabCtxMenuRef, handleCloseTabCtxMenu, !!tabCtxMenu);

  // 탭관리 메뉴 항목 - 헤더 드롭다운과 활성 탭 우클릭 메뉴가 같은 항목을 공유(단일 소스).
  // 각 항목의 useTabs 함수가 closeDropdowns()를 호출하므로 클릭 후 두 메뉴 모두 자동으로 닫힘.
  const renderCtrlTabsMenuItems = () => (
    <>
      <button
          type="button"
          onClick={refreshAllTabs}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer bg-transparent border-none"
      >
          <i className="fas fa-sync w-4"></i> {ddKey('lblRefreshAllTabs') || '전체 새로고침'}
      </button>
      <hr className="my-1 border-gray-200" />
      <button
          type="button"
          onClick={closeCurrentTab}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer bg-transparent border-none"
      >
          <i className="fas fa-times-circle w-4"></i> {ddKey('lblCloseCurrentTab') || '현재 탭 닫기'}
      </button>
      <button
          type="button"
          onClick={closeOtherTabs}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer bg-transparent border-none"
      >
          <i className="fas fa-minus-circle w-4"></i> {ddKey('lblCloseOthersTab') || '다른 탭 모두 닫기'}
      </button>
      <hr className="my-1 border-gray-200" />
      <button
          type="button"
          onClick={closeLeftTabs}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer bg-transparent border-none"
      >
          <i className="fas fa-arrow-left w-4"></i> {ddKey('lblCloseLeftTabs') || '왼쪽 탭 모두 닫기'}
      </button>
      <button
          type="button"
          onClick={closeRightTabs}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer bg-transparent border-none"
      >
          <i className="fas fa-arrow-right w-4"></i> {ddKey('lblCloseRightTabs') || '오른쪽 탭 모두 닫기'}
      </button>
      <hr className="my-1 border-gray-200" />
      <button
          type="button"
          onClick={closeAllTabs}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer bg-transparent border-none"
      >
          <i className="fas fa-times w-4"></i> {ddKey('lblCloseAllTabs') || '모든 탭 닫기'}
      </button>
    </>
  );

  // Load available languages from config.json
  React.useEffect(() => {
    axios.get('/config.json')
      .then(res => {
        const cfg = res.data;
        if (Array.isArray(cfg.languages)) {
          getLanguages(cfg.languages);
        }
      })
      .catch(() => {});
  }, []);

  // 다국어 라벨(ZA_DD) 로드 - 로그인 시 1회, 5개 언어를 한번에 받아오므로 언어 변경 시 재조회 불필요
  React.useEffect(() => {
    const loginUserInfo = getUserInfo();
    axios.post(`${API_BASE_URL}/com/apiDataDictionary`, {data: {langCd: language, userId: loginUserInfo?.userId }})
      .then(res => {
        const rsResults = res.data; // apiDataDictionary는 { ko: {...}, en: {...}, ... }를 감싸지 않고 그대로 반환
        if (rsResults && typeof rsResults === 'object') {
          setDDData(rsResults);
        } else {
          console.error('Failed to load DD label data:', rsResults);
        }
      })
      .catch(error => console.error('Failed to load DD label data:', error));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDDData]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 사용자 Effect 
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 메뉴 데이터 로드 (로그인 사용자 권한 기반 - DB조회)
  //  - language가 변경되면(사용자가 언어 선택) 해당 언어로 메뉴를 다시 조회
  useEffect(() => {
    let cancelled = false;
    const loadMenuData = async () => {
      try {
        const loginUserInfo = getUserInfo();
        const rsRowData = await axios.post(`${API_BASE_URL}/com/apiMainMenuTree`, {
          data: {langCd: language, userId: loginUserInfo?.userId }
        });
        if (cancelled) return; // 이후 language가 다시 바뀌어 이 요청이 새 요청보다 늦게 도착한 경우 - 낡은 응답 무시
        const rsResults = rsRowData.data; // apiMainMenuTree는 { mainMenuData, subMenuData }를 감싸지 않고 그대로 반환
        setLicenseReadOnly(!!rsResults?.license?.readOnly);   // 라이선스 만료 후 읽기 전용이면 메뉴바 저장/삭제 버튼 비활성(NaviMenuBar)
        setSolutionTitle(rsResults?.solutionTitle || '');
        setSolutionTitleCache(rsResults?.solutionTitle || ''); // 다른 화면(X1002Q1/IframeTab 등)이 prop 없이 getSolutionTitle()로 바로 쓸 수 있게 캐싱
        // HOME 메뉴는 DB(ZA_MENU_LANG) 데이터 유무와 무관하게 항상 좌측 최상단에 고정 노출 - 언어 번역만 ddKey('Home')으로 적용
        const dbMenuData: any[] = rsResults?.mainMenuData || [];
        const homeItem = { key: 'Home', title: 'Home', url: '', icon: 'fas fa-house', hasSubMenu: false };
        const isHomeDbItem = (item: any) => String(item.linkKey || '').trim().toUpperCase() === 'HOME' || String(item.title || '').trim().toUpperCase() === 'HOME';
        const otherMenuData = dbMenuData.filter(item => item.key !== 'Home' && !isHomeDbItem(item));
        // ZA_MENU.LINK_KEY='HOME' 메뉴의 CALL_PGM_ID로 홈 컴포넌트 결정(없으면 '' -> 페이지없음)
        const homeDbItem = dbMenuData.find(item => String(item.linkKey || '').trim().toUpperCase() === 'HOME');
        setMenuHomePgmId(String(homeDbItem?.pgmId || '').trim());
        setMainMenuData([homeItem, ...otherMenuData]);
        const freshSubMenuData = rsResults?.subMenuData || {};
        setSubMenuData(freshSubMenuData);

        // 열려있는 탭 제목도 방금 새 언어로 재조회한 메뉴데이터 기준으로 갱신
        //  - 메뉴 탭 제목은 ZA_MENU_LANG(이 응답)에서 오는 것이라 ddKey()만으로는 재번역이 안 됨(useTabs 쪽 주석 참고)
        refreshTabTitles((tab) => {
          const mainItem = [homeItem, ...otherMenuData].find((m: any) => m.key === tab.id);
          if (mainItem) {
            const title = ddKey(tab.id) || mainItem.title || tab.id;
            return { title, menuPath: title };
          }
          if (tab.parentMenuKey) {
            const parentTitle = ddKey(tab.parentMenuKey) || [homeItem, ...otherMenuData].find((m: any) => m.key === tab.parentMenuKey)?.title || tab.parentMenuKey;
            const items = freshSubMenuData[tab.parentMenuKey]?.items || [];
            // 서브메뉴는 items[]에 직접 있거나(2단), items[].children[]에 중첩(3단)되어 있을 수 있음
            const subItem = items.find((it: any) => it.itemKey === tab.id)
              ?? items.flatMap((it: any) => it.children || []).find((it: any) => it.itemKey === tab.id);
            if (subItem) {
              const title = ddKey(tab.id) || subItem.itemName || tab.id;
              const nameOf = (it: SubMenuItem) => ddKey(it.itemKey) || it.itemName || it.itemKey;
              return { title, menuPath: buildMenuPath(parentTitle, items, tab.id, nameOf) || `${parentTitle} > ${title}` };
            }
          }
          // 메뉴트리에 없는 탭(X7001M1 등 사용자드롭다운 전용) - ddKey에 등록된 키면 그걸로 갱신
          const ddTitle = ddKey(tab.originalKey || tab.id);
          return ddTitle ? { title: ddTitle, menuPath: ddTitle } : null;
        });
      } catch (error) {
        if (!cancelled) console.error('Failed to load menu data:', error);
      }
    };

    loadMenuData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // 브랜드 텍스트(solutionTitle) 자동 폰트크기 축소 - .env(SOLUTION_TITLE) 길이가 배포마다 달라서
  // CSS max-width(.brand-text)만으로는 넘치는 글자가 그냥 잘려버림. 실제로 넘칠 때만 스크롤폭에 맞춰
  // 폰트크기를 줄여서(최소값 있음) 잘리지 않고 전체 글자가 보이게 함.
  useLayoutEffect(() => {
    const el = brandTextRef.current;
    if (!el) return;
    const MIN_FONT_PX = 11;
    const fit = () => {
      el.style.fontSize = '';               // CSS 캐스케이드 기준 크기로 리셋 후 다시 측정
      const baseFontPx = parseFloat(getComputedStyle(el).fontSize) || 16;
      const overflowRatio = el.clientWidth > 0 ? el.scrollWidth / el.clientWidth : 1;
      if (overflowRatio > 1) {
        const fitted = Math.max(MIN_FONT_PX, Math.floor(baseFontPx / overflowRatio));
        el.style.fontSize = `${fitted}px`;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [solutionTitle]);

  // 브라우저 탭 제목도 같은 SOLUTION_TITLE로 - 값이 없으면(.env 미설정) index.html의 기본 <title>을 그대로 둠
  useEffect(() => {
    if (solutionTitle) document.title = solutionTitle;
  }, [solutionTitle]);

  // 사용자 정보 로드
  useEffect(() => {
    const fetchServerUserConfig = (base: any) => {
      axios.post(`${API_BASE_URL}/com/apiUserInfo`, { userId: base.userId })
        .then(res => {
          const ui = res.data?.userInfo;
          if (!ui) return;
          const merged = { ...(getUserInfo() || base), userKind: String(ui.userKind ?? ''), sessExtYn: ui.sessExtYn ?? 'N', sessLimitMin: ui.sessLimitMin ?? 60, menuIdViewYn: ui.menuIdViewYn ?? 'N', noticeCoYn: ui.noticeCoYn ?? 'N', noticeBuYn: ui.noticeBuYn ?? 'N', noticeCmYn: ui.noticeCmYn ?? 'N', schdCoYn: ui.schdCoYn ?? 'N', schdBuYn: ui.schdBuYn ?? 'N', schdCmYn: ui.schdCmYn ?? 'N', chatReqYn: ui.chatReqYn ?? 'N', chatCnslYn: ui.chatCnslYn ?? 'N', ddCrtYn: ui.ddCrtYn ?? 'N', coNm: ui.coNm ?? '', serverInfo: ui.serverInfo ?? {} };
          try { localStorage.setItem('userInfo', JSON.stringify(merged)); } catch { /* 저장 실패는 무시 */ }
          setUserInfo(merged);
        })
        .catch(() => { /* 조회 실패 시 기존 값 유지 */ });
    };

    const loadUserInfo = () => {
      const userInfo = getUserInfo();
      setUserInfo(userInfo);
      // 사용자가 선택한 언어가 있으면 적용
      if (userInfo?.language) {
        getLanguage(userInfo.language as any);
      }
      // 서버의 최신 사용자정보 보충 - 사용자유형(userKind)뿐 아니라 사용자환경설정(Z3020M1)의 값(연장가능/접속제한시간/메뉴ID 보기)은
      // 관리자가 로그인 이후에 바꿀 수 있으므로, 화면을 열거나 새로고침할 때마다 서버에서 다시 받아 반영함.
      // loginTime은 건드리지 않으려고 setLocalStorage 대신 직접 병합해 저장함.
      if (userInfo?.userId) fetchServerUserConfig(userInfo);
    };

    // 초기 로드
    loadUserInfo();

    // 사용자 정보 변경 이벤트 리스너
    const handleUserInfoChange = (event: CustomEvent) => {
      const newUserInfo = event.detail;
      setUserInfo(newUserInfo);
      // 새로운 사용자 정보에 언어가 있으면 적용
      if (newUserInfo?.language) {
        getLanguage(newUserInfo.language as any);
      }
    };

    window.addEventListener('userInfoChanged', handleUserInfoChange as EventListener);
    // 사용자환경설정(Z3020M1)에서 본인 설정을 저장했을 때 - 서버 값을 다시 받아 바로 반영(메뉴ID 보기 등)
    const handleUserConfigChanged = () => { const cur = getUserInfo(); if (cur?.userId) fetchServerUserConfig(cur); };
    window.addEventListener('userConfigChanged', handleUserConfigChanged);

    return () => {
      window.removeEventListener('userInfoChanged', handleUserInfoChange as EventListener);
      window.removeEventListener('userConfigChanged', handleUserConfigChanged);
    };
  }, [getLanguage, navigate]);

  // 스크롤바 자동 숨김/표시 기능 및 맨 위로 가기 버튼 / 탭 스크롤 좌우 버튼
  const { showScrollToTop, showScrollToBottom } = useContentScrollButtons([openTabs]);
  const { showTabScrollLeft, showTabScrollRight } = useTabScrollButtons([openTabs]);
  const { showSidebarScrollUp, showSidebarScrollDown } = useSidebarScrollButtons([mainMenuData, menuStyle, isSidebarCollapsed]);

  // 서브사이드바 폭 드래그 리사이즈 - .sub-sidebar-resizer(경계선)를 mousedown하면 시작, mousemove로 폭 갱신,
  // mouseup에서 종료 + localStorage 저장. SplitContainer의 드래그 방식과 동일한 느낌(드래그 중 커서/트랜지션
  // 처리)이지만, 그리드 분할과 달리 퍼센트가 아니라 min~max px로 clamp함(사이드바는 화면 폭과 무관하게
  // 사용자가 정한 픽셀값을 유지해야 함).
  const handleSubSidebarResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    isSidebarDraggingRef.current = true;
    setIsSubSidebarDragging(true);
    document.body.style.cursor = 'col-resize';
  }, []);
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!isSidebarDraggingRef.current) return;
      const subSidebarEl = document.getElementById('subSidebar');
      if (!subSidebarEl) return;
      const rect = subSidebarEl.getBoundingClientRect();
      const next = Math.max(SUB_SIDEBAR_MIN_WIDTH, Math.min(SUB_SIDEBAR_MAX_WIDTH, Math.round(e.clientX - rect.left)));
      setSubSidebarWidth(next);
    };
    const onUp = () => {
      if (!isSidebarDraggingRef.current) return;
      isSidebarDraggingRef.current = false;
      setIsSubSidebarDragging(false);
      document.body.style.cursor = '';
      setSubSidebarWidth(w => {
        try { localStorage.setItem(SUB_SIDEBAR_WIDTH_KEY, String(w)); } catch { /* 저장소 접근 실패 시 무시 - 이번 세션에서만 유지 */ }
        return w;
      });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 전역 함수로 탭 추가 함수 노출 : 점프하는 곳에서 탭추가함.
  // <ComponentRenderer url={tab.url} paramData={tab.paramData} autoQuery={tab.autoQuery} />
  useEffect(() => {
    window.addTab = (tabInfo) => {
      addTab(tabInfo);
    };
    return () => {
      window.addTab = undefined;
    };
  }, [addTab]);

  // 전역 함수로 탭 닫기 함수 노출 : 화면 스스로 자신의 탭을 닫을 때 사용(예: S5003M1처럼 외부 콘솔만 띄우고 탭은 닫는 경우)
  useEffect(() => {
    window.closeTab = (tabId) => {
      closeTab(tabId);
    };
    return () => {
      window.closeTab = undefined;
    };
  }, [closeTab]);

  // 전역 함수로 공지사항 알림 새로고침 노출 : X1002Q1 목록 등 다른 탭에서 읽음처리(apiX1002Q1ReadOne) 후
  // 5분 주기를 기다리지 않고 mainFrame 벨 드롭다운 상태를 즉시 최신화하도록 호출.
  useEffect(() => {
    window.refreshNoticeAlerts = () => {
      checkNewNotices();
    };
    return () => {
      window.refreshNoticeAlerts = undefined;
    };
  }, [checkNewNotices]);

  // 전역 함수로 탭 타이틀 변경 노출 : 새 탭을 만들지 않고 같은 탭 안에서 화면만 전환하는 페이지
  // (예: X1002Q1의 목록/상세 토글)가 자신이 속한 탭의 타이틀을 스스로 바꿀 때 사용.
  useEffect(() => {
    window.setTabTitle = (tabId, title) => {
      setTabTitle(tabId, title);
    };
    return () => {
      window.setTabTitle = undefined;
    };
  }, [setTabTitle]);

  // 메인 메뉴 클릭 핸들러
  const handleMainMenuClick = (menuKey: string, hasSubMenu: boolean, url: string) => {
    // 현재 메뉴의 아이콘 정보 가져오기
    const currentMenuItem = mainMenuData.find(item => item.key === menuKey);
    const menuIcon = currentMenuItem?.icon;
    // 외부URL 메뉴 - 하위메뉴 없이 화면을 바로 여는 단독 메인메뉴의 CALL_PGM_ID가 URL이면(또는 LINK_KEY='EXTURL')
    // 내부 탭 대신 새 브라우저 탭으로 그대로 엶. 메뉴/탭 상태는 건드리지 않음.
    if (isExternalUrlLink(url, currentMenuItem?.linkKey) && url) {
      openExternalUrlTab(url);
      return;
    }
    // DB에서 내려오는 HOME 메뉴는 key가 고정 'Home'이 아니라 메뉴ID(예: 'k0')라서 이름으로도 판별
    const isHomeMenu = menuKey === 'Home' || (currentMenuItem?.title || '').trim().toUpperCase() === 'HOME';

    if (isHomeMenu) {
      // 홈 탭은 항상 그대로 열되(activeMainMenu는 건드리지 않음), 서브 사이드바만 다른 메뉴처럼 토글(보이기/숨기기)
      setShowSubSidebar(prev => {
        if (prev) return false; // 열려있던 서브메뉴 닫기
        return !!(activeMainMenu && subMenuData[activeMainMenu]); // 닫혀있었다면 이전에 보던 서브메뉴가 있을 때만 다시 보이기
      });
      addTab({
        id: 'Home',
        title: ddKey('Home') || 'Home',
        url: 'HomePage', // 특별한 URL로 HomePage 컴포넌트임을 표시
        originalKey: 'Home',
        menuPath: ddKey('Home') || 'Home'
      });
      return;
    }
    
    if (hasSubMenu) {
      if (activeMainMenu === menuKey && showSubSidebar) {
        // 이미 열려있는 메뉴를 다시 클릭하면 닫기
        setShowSubSidebar(false);
        setActiveMainMenu('');
        setExpandedSubMenus(new Set()); // 하위 메뉴 초기화
      } else {
        // 새로운 메뉴 열기
        setActiveMainMenu(menuKey);
        setShowSubSidebar(true);
        setExpandedSubMenus(new Set()); // 하위 메뉴 초기화
      }
    } else {
      setActiveMainMenu(menuKey);
      setShowSubSidebar(false);
      setExpandedSubMenus(new Set()); // 하위 메뉴 초기화
      // 단일 메뉴는 직접 탭 생성
      if (url) {
        addTab({
          id: menuKey,
          title: ddKey(menuKey) || currentMenuItem?.title || menuKey,
          url: url,
          originalKey: menuKey,
          menuPath: ddKey(menuKey) || currentMenuItem?.title || menuKey,
          icon: menuIcon, // 메인 메뉴 아이콘 추가
          paramData: currentMenuItem?.paramData, // ZA_MENU.PARAM(JSON) - 화면에 넘길 고정 파라미터
          linkKey: currentMenuItem?.linkKey, // 'ADDTAB'이면 탭 안 내용을 IframeTab(외부URL)으로 렌더링
        });
        // 하위메뉴 없는 단독 메인메뉴가 공지사항(LINK_KEY='NOTICE')이면 서브메뉴 항목 클릭과 동일하게 목록으로 되돌림
        if (currentMenuItem?.linkKey === 'NOTICE') {
          window.gotoNoticeList?.();
        }
      }
    }
  };

  // 서브 메뉴 클릭 핸들러
  const handleSubMenuClick = (item: SubMenuItem) => {
    if (item.itemKey === '-') return; // 구분선은 클릭 불가
    
    // children이 있으면 토글, 없으면 탭 생성 (children이 빈 배열([])이고 url도 없는 빈 메뉴는 클릭 무시)
    if (Array.isArray(item.children) && item.children.length > 0) {
      toggleSubMenu(item.itemKey);
    } else if (!item.url) {
      return;
    } else if (isExternalUrlLink(item.url, item.linkKey)) {
      // 외부URL 메뉴 - 내부 탭 대신 새 브라우저 탭으로 CALL_PGM_ID(=item.url)를 그대로 엶
      openExternalUrlTab(item.url);
      if (!subSidebarPinned) setShowSubSidebar(false);
    } else {
      const mainMenuTitle = ddKey(activeMainMenu) || mainMenuData.find(m => m.key === activeMainMenu)?.title || activeMainMenu;
      const subMenuTitle = ddKey(item.itemKey) || (item.itemName ? item.itemName : item.itemKey);
      addTab({
        id: item.itemKey,
        title: subMenuTitle,
        url: item.url,
        originalKey: item.itemKey,
        menuPath: menuPathFor(activeMainMenu, item.itemKey) || `${mainMenuTitle} > ${subMenuTitle}`, // 메인메뉴 > 그룹 > 화면
        parentMenuKey: activeMainMenu,
        autoQuery: item.autoQuery,
        icon: item.icon, // 서브메뉴 아이콘 추가
        paramData: item.paramData, // ZA_MENU.PARAM(JSON) - 같은 화면을 공유하는 메뉴별 고정 파라미터
        linkKey: item.linkKey, // 'ADDTAB'이면 탭 안 내용을 IframeTab(외부URL)으로 렌더링
      });
      if (!subSidebarPinned) setShowSubSidebar(false);
      // 2026-09-20: 사이드메뉴에서 직접 공지사항(LINK_KEY='NOTICE') 메뉴를 클릭한 경우도, 벨 드롭다운의
      // "공지사항조회"와 동일하게 동작해야 함 - 이미 열린 탭이 상세화면을 보고 있었다면 목록으로 되돌림.
      if (item.linkKey === 'NOTICE') {
        window.gotoNoticeList?.();
      }
      // 좁은 모바일에서는 서브사이드바가 화면을 덮는 오버레이라, 화면을 고르면 닫아서 열린 화면이 바로 보이게 함
      if (window.matchMedia('(max-width: 600px)').matches) setShowSubSidebar(false);
    }
  };
  // 헤더 메뉴 검색 - 메뉴명/메뉴ID(ZA_MENU.MENU_ID)/화면ID를 입력하면 자동완성 목록이 뜨고, 선택(클릭/Enter)하면 해당 메뉴를 호출함.
  //  검색 대상은 이 사용자가 접근 가능한(사이드메뉴 트리에 내려온) 메뉴뿐이라 권한 없는 메뉴는 나오지 않음.
  //  · 화면이 있는 메뉴 -> 탭 열기(사이드메뉴 항목 클릭과 동일), 하위메뉴가 있는 메인메뉴/그룹 -> 사이드메뉴를 펼쳐서 보여줌
  type MenuHit =
    | { kind: 'main'; item: MainMenuItem }
    | { kind: 'group'; item: SubMenuItem; groupKey: string }
    | { kind: 'leaf'; item: SubMenuItem; groupKey: string };
  interface SearchableMenu { hit: MenuHit; id: string; url: string; name: string; path: string }
  const allSearchableMenus = useMemo(() => {
    const list: SearchableMenu[] = [];
    mainMenuData.forEach(m => list.push({ hit: { kind: 'main', item: m }, id: m.key, url: m.url || '', name: ddKey(m.key) || m.title || m.key, path: '' }));
    Object.entries(subMenuData).forEach(([groupKey, group]) => {
      const mainName = ddKey(groupKey) || mainMenuData.find(m => m.key === groupKey)?.title || groupKey;
      const nameOf = (it: SubMenuItem) => ddKey(it.itemKey) || it.itemName || it.itemKey;
      group.items.forEach(it => {
        if (it.itemKey === '-') return;
        if (Array.isArray(it.children) && it.children.length > 0) {
          list.push({ hit: { kind: 'group', item: it, groupKey }, id: it.itemKey, url: '', name: nameOf(it), path: mainName });
          it.children.forEach(ch => { if (ch.url) list.push({ hit: { kind: 'leaf', item: ch, groupKey }, id: ch.itemKey, url: ch.url, name: nameOf(ch), path: `${mainName} > ${nameOf(it)}` }); });
        } else if (it.url) {
          list.push({ hit: { kind: 'leaf', item: it, groupKey }, id: it.itemKey, url: it.url, name: nameOf(it), path: mainName });
        }
      });
    });
    return list;
  }, [mainMenuData, subMenuData, ddKey]);
  // 자동완성 결과(최대 10건) - 메뉴ID/화면ID 일치 > 시작 일치 > 메뉴명 시작 > 포함 순
  const menuSearchResults = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return [] as SearchableMenu[];
    const score = (m: SearchableMenu): number => {
      const id = m.id.toLowerCase(), nm = m.name.toLowerCase(), pg = pgmIdOf(m.url).toLowerCase();
      if (id === q || pg === q || nm === q) return 0; // 메뉴ID/화면ID/메뉴명이 정확히 일치
      if (id.startsWith(q) || (pg && pg.startsWith(q))) return 1;
      if (nm.startsWith(q)) return 2;
      if (id.includes(q) || (pg && pg.includes(q))) return 3;
      if (nm.includes(q)) return 4;
      return -1;
    };
    return allSearchableMenus
      .filter(m => m.id !== 'Home')
      .map(m => ({ m, s: score(m) }))
      .filter(x => x.s >= 0)
      .sort((x, y) => x.s - y.s || x.m.id.localeCompare(y.m.id))
      .slice(0, 10)
      .map(x => x.m);
  }, [menuSearch, allSearchableMenus]);
  // 검색된 메뉴를 실제로 호출
  const openMenuHit = (hit: MenuHit) => {
    if (hit.kind === 'main') {
      if (hit.item.hasSubMenu) {
        // 하위메뉴가 있는 메인메뉴 -> 사이드메뉴 펼치기
        setActiveMainMenu(hit.item.key);
        setShowSubSidebar(true);
        setExpandedSubMenus(new Set());
      } else {
        handleMainMenuClick(hit.item.key, false, hit.item.url);
      }
    } else if (hit.kind === 'group') {
      setActiveMainMenu(hit.groupKey);
      setShowSubSidebar(true);
      setExpandedSubMenus(new Set([hit.item.itemKey]));
    } else {
      const item = hit.item;
      if (isExternalUrlLink(item.url, item.linkKey)) {
        // 외부URL 메뉴 - 내부 탭 대신 새 브라우저 탭으로 CALL_PGM_ID(=item.url)를 그대로 엶
        openExternalUrlTab(item.url || '');
      } else {
        const mainMenuTitle = ddKey(hit.groupKey) || mainMenuData.find(m => m.key === hit.groupKey)?.title || hit.groupKey;
        const subMenuTitle = ddKey(item.itemKey) || item.itemName || item.itemKey;
        setActiveMainMenu(hit.groupKey);
        addTab({
          id: item.itemKey,
          title: subMenuTitle,
          url: item.url,
          originalKey: item.itemKey,
          menuPath: menuPathFor(hit.groupKey, item.itemKey) || `${mainMenuTitle} > ${subMenuTitle}`,
          parentMenuKey: hit.groupKey,
          autoQuery: item.autoQuery,
          icon: item.icon,
          paramData: item.paramData,
          linkKey: item.linkKey, // 'ADDTAB'이면 탭 안 내용을 IframeTab(외부URL)으로 렌더링
        });
        if (item.linkKey === 'NOTICE') {
          window.gotoNoticeList?.();
        }
      }
    }
    setMenuSearch('');
    setMenuSearchOpen(false);
    setMenuSearchIdx(-1);
  };
  // 홈 인사말 바로가기 버튼("수수료 조회" 등) - CALL_PGM_ID(화면ID)로 사용자 메뉴를 찾아 openMenuHit과 동일하게 탭을 엶.
  // 메뉴 검색과 같은 소스(allSearchableMenus)를 쓰므로 그 사용자 메뉴에 없는 화면이면(권한 없음) 아무 동작도 하지 않음.
  const openMenuByPgmId = (pgmId: string) => {
    const found = allSearchableMenus.find(m => pgmIdOf(m.url).toLowerCase() === pgmId.toLowerCase());
    if (found) openMenuHit(found.hit);
  };
  // Enter/돋보기 클릭 - 목록에서 고른 항목 > 메뉴ID/화면ID/메뉴명 정확히 일치 > 자동완성 첫 항목 순으로 호출, 없으면 안내
  const runMenuSearch = () => {
    const q = menuSearch.trim();
    if (!q) return;
    const key = q.toLowerCase();
    const target = (menuSearchIdx >= 0 ? menuSearchResults[menuSearchIdx] : undefined)
      ?? allSearchableMenus.find(m => m.id.toLowerCase() === key)
      ?? allSearchableMenus.find(m => m.url && pgmIdOf(m.url).toLowerCase() === key)
      ?? allSearchableMenus.find(m => m.id !== 'Home' && m.name.toLowerCase() === key) // 메뉴명이 정확히 일치
      ?? menuSearchResults[0]; // 그 외에는 자동완성의 첫 항목(메뉴명/메뉴ID 부분 일치)
    if (!target) {
      showMsgBox({ title: ddKey('lblAlertTitle') || '알림', msgText: `[${q}] ${ddKey('msgMenuNotFound') || '메뉴를 찾을 수 없습니다. 메뉴명/메뉴ID를 확인하세요(권한이 없는 메뉴는 호출할 수 없습니다).'}`, showBtn: 'OK', msgType: 'warning' });
      return;
    }
    openMenuHit(target.hit);
  };

  // 하위 메뉴 토글 함수
  const toggleSubMenu = (itemKey: string) => {
    setExpandedSubMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  // 서브메뉴 렌더링 컴포넌트
  const renderSubMenuItem = (item: SubMenuItem): React.ReactElement => {
    if (item.itemKey === '-') {
      return (
        <li key={Math.random()} className="submenu-separator">
          <hr className="submenu-separator-hr" />
        </li>
      );
    }
    // 서브메뉴에서 자메뉴가 있는지 체크
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const isExpanded = expandedSubMenus.has(item.itemKey);
    // 사용자환경설정(Z3020M1)의 [메뉴ID 보기]가 켜진 사용자는 메뉴명 뒤에 MENU_ID를 함께 표기 - "메뉴명(MENU_ID)"
    
    const baseMenuLabel = ddKey(item.itemKey) || (item.itemName ? item.itemName : item.itemKey);
    const menuLabel = userInfo?.menuIdViewYn === 'Y' && item.itemKey !== baseMenuLabel ? `${baseMenuLabel}(${item.itemKey})` : baseMenuLabel;
    // 클릭된(현재 열려서 활성 탭인) 메뉴 항목 표시 - 그룹(children 있음)은 탭이 아니라 항상 제외됨
    const isSelected = !hasChildren && openTabs.some(tab => tab.active && (tab.originalKey === item.itemKey || tab.id === item.itemKey));

    return (
      <li key={item.itemKey} id={`sideMenuItem-${item.itemKey}`} className={hasChildren ? 'has-submenu' : ''}>
        <button
          type="button"
          onClick={() => handleSubMenuClick(item)}
          title={menuLabel}
          className={`submenu-item ${hasChildren ? 'submenu-toggle' : ''} ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected' : ''}`}
        >
          <span className="submenu-content">
            <i className={`icon ${item.icon}`}></i>
            <span>{menuLabel}</span>
          </span>
          {hasChildren && (
            <i
              className={`fas fa-${isExpanded ? 'minus' : 'plus'} submenu-arrow`}
            ></i>
          )}
        </button>
        {hasChildren && (
          <ul 
            className={`submenu-children ${isExpanded ? 'expanded' : ''}`}
          >
            {Array.isArray(item.children) && item.children.map(child => renderSubMenuItem(child))}
          </ul>
        )}
      </li>
    );
  };

  // 로그아웃 핸들러
  const handleLogout = async () => {
    const message = ddKey('msgConfirmLogout') || '로그아웃 하시겠습니까?';
    //if (window.confirm(message)) {
    const confirmed = await confirmMsgBox({ title:ddKey('lblConfirm') || "확인", msgText: message, showBtn: "YesNo" });
    if (confirmed)
    { 
      // 로그인 이력(ZA_LOGIN_HIST) 기록 + 세션쿠키 삭제(httpOnly라 서버만 지울 수 있음) - 실패해도 로그아웃은 진행
      try { await axios.post(`${API_BASE_URL}/com/apiLoginLogout`, { clientInfo: getClientInfo(language) }, { timeout: 3000 }); } catch { /* DB 연결이 안 돼도 로그아웃은 계속 진행 */ }
      // 사용자 정보 초기화
      clearUserInfo(); // 사용자 정보 삭제
      localStorage.removeItem('tokenExpiresAt');
      // 로그아웃 후 홈페이지로 리다이렉트
      navigate('/');
    }    
  };

  // 맨 위로 가기 함수
  const scrollToTop = () => {
    const activeTabElement = document.querySelector('.custom-scrollbar.active') as HTMLElement;
    if (activeTabElement) {
      activeTabElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // 맨 아래로 가기 함수
  const scrollToBottom = () => {
    const activeTabElement = document.querySelector('.custom-scrollbar.active') as HTMLElement;
    if (activeTabElement) {
      activeTabElement.scrollTo({
        top: activeTabElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // 탭 좌측 스크롤 함수
  const scrollTabsLeft = () => {
    const tabContainer = document.getElementById('mainContentOpenTabs');
    if (tabContainer) {
      tabContainer.scrollBy({
        left: -200, // 200px 왼쪽으로 스크롤
        behavior: 'smooth'
      });
    }
  };

  // 탭 우측 스크롤 함수
  const scrollTabsRight = () => {
    const tabContainer = document.getElementById('mainContentOpenTabs');
    if (tabContainer) {
      tabContainer.scrollBy({
        left: 200, // 200px 오른쪽으로 스크롤
        behavior: 'smooth'
      });
    }
  };

  // 좌측 메인사이드바 메뉴 상/하 스크롤 함수 - 고정 픽셀이 아니라 항목(li) 1개 높이만큼만 이동
  const scrollSidebarMenu = (direction: 'up' | 'down') => {
    const menuContainer = document.getElementById('mainMenu');
    if (!menuContainer) return;
    const itemHeight = (menuContainer.querySelector('li') as HTMLElement | null)?.offsetHeight || 80;
    menuContainer.scrollBy({
      top: direction === 'up' ? -itemHeight : itemHeight,
      behavior: 'smooth'
    });
  };
  
  // 사용자영역 아이콘 클릭 - 프로필정보+개인정보변경/비밀번호변경/DD생성/로그아웃을 하나로 합친 패널을 토글.
  // X7001M1(개인정보변경) 화면과 동일한 조회 API로 프로필정보를 가져옴(메가메뉴와 동일한 앵커형 드롭다운 스타일).
  const handleToggleUserPanel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCtrlTabsDropdown(false);
    setShowNoticeDropdown(false);
    const opening = !showUserDropdown;
    setShowUserDropdown(opening);
    if (!opening || !userInfo?.userId) return;
    // 패널은 바로 열어 두고(로그아웃 등 버튼은 항상 보임), 개인정보는 백그라운드로 받아서 채움
    setProfileData(null);
    setProfileError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/za/apiX7001M1G1`, { data: {} }, { timeout: 8000 });
      const rsResults = res?.data?.rsResults;
      const row = Array.isArray(rsResults) && rsResults.length > 0 ? rsResults[0] : null;
      setProfileData(row);
    } catch (error) {
      setProfileData(null);
      setProfileError(getMsgText(error, 'DB 연결 오류로 개인정보를 불러오지 못했습니다.'));
    }
  };

  // 사용자 드롭다운 메뉴 항목들
  const handleProfileEdit = () => {
    addTab({
      id: 'X7001M1',
      title: ddKey('X7001M1') || '개인정보변경',
      url: 'za/X7001M1', // React 컴포넌트 또는 URL
      originalKey: 'X7001M1',
      menuPath: ddKey('X7001M1') || '개인정보변경',
      paramData: { userId: userInfo?.userId || '', userName: userInfo?.userName || '' },
      autoQuery: true, // JumpIn하는 페이지에서 조회버튼 자동클릭
      icon: 'fas fa-id-card', // 프로필 편집 아이콘
    });
    setShowUserDropdown(false);
    setShowCtrlTabsDropdown(false);
    setShowNoticeDropdown(false);
  };

  // 비밀번호변경 - 별도 탭/화면을 여는 대신 LoginPage의 강제 비밀번호변경과 동일한 팝업(ChangePasswordModal)을 사용.
  //  currentPassword를 안 넘기므로 팝업이 "현재 비밀번호" 입력창을 추가로 보여줌(로그인 상태와 무관하게 본인 확인 필요).
  const handlePasswordEdit = () => {
    setPwdChangeOpen(true);
    setShowUserDropdown(false);
    setShowCtrlTabsDropdown(false);
    setShowNoticeDropdown(false);
  };

  // DD생성 : 활성화된 화면의 필드라벨을 DB(ZA_DD)에 언어별로 번역해서 생성 (구현 예정)
  const handleGenDD = async () => {
    setShowUserDropdown(false);
    setShowCtrlTabsDropdown(false);
    setShowNoticeDropdown(false);

    const activeTab = openTabs.find(tab => tab.active);
    // if (!activeTab || activeTab.url === 'HomePage') {
    if (!activeTab) {
      showMsgBox({ title: "알림", msgText: "DD생성을 실행할 화면을 먼저 여세요.", showBtn: "OK", msgType: "warning" });
      return;
    }

    const activePgmId = activeTab.url === 'HomePage' ? 'HOMEPAGE' : (activeTab.url.split('/')[1]?.toUpperCase() || activeTab.url);
    const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: `${activeTab.title}[${activePgmId}] : ${ddKey('msgConfirmGenDD') || 'DD생성 하시겠습니까?'}`, showBtn: "YesNo" });
    if (!confirmed) return;

    try {
      // menuId(=activeTab.id, ZA_MENU.MENU_ID) 기준으로 저장하도록 함께 전달 - url은 여전히 스캔할
      // .tsx 소스파일 경로를 찾는 데만 쓰이고, ZA_DD 저장 키는 menuId로 바뀜(2026-09-18).
      // 홈 탭은 tab.id가 'Home'(고정)이라 ZA_MENU에 없는 값 - HomePage.tsx가 여전히 'HOMEPAGE' 기준으로
      // 조회하므로(DD 조회 키 'HOMEPAGE', ComponentRenderer를 안 거치는 특수 케이스) 저장 키도 맞춰줌.
      const menuIdForGenDD = activeTab.url === 'HomePage' ? 'HOMEPAGE' : activeTab.id;
      const res = await axios.post(`${API_BASE_URL}/com/apiGenDD`, {langCd: language, url: activeTab.url, userId: userInfo?.userId, menuId: menuIdForGenDD });
      const rsResults = res.data;
      showMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: `[${rsResults?.menuId}] 화면에서 ${rsResults?.count ?? 0}개의 라벨을 DD에 생성했습니다.`, showBtn: "OK", msgType: "info" });
    } catch (error: any) {
      showMsgBox({ title: ddKey('lblError') || "오류", msgText: getMsgText(error, 'DD생성 중 오류가 발생했습니다.'), showBtn: "OK", msgType: getMsgType(error) });
    }
  };

  // 토큰 만료 감시(확인/리다이렉션/남은시간 카운트다운)
  const { tokenExpiry, tokenRemainHMS, showExtendPrompt, dismissExtendPrompt, extendTokenExpiry } = useTokenExpiry(navigate);

  // 세션 연장 확인창 -> 확인시 apiUserToken으로 토큰 재발급받아 최초 발급 때와 동일한 시간으로 다시 시작.
  //   . 남은시간이 1분 이하가 되면 useTokenExpiry가 showExtendPrompt를 true로 올려 자동으로 호출됨(아래 useEffect)
  //   . 모래시계 아이콘을 직접 클릭해서 수동으로도 호출 가능(handleTimerIconClick)
  const handleExtendSession = useCallback(async (manual = false) => {
    // 사용자환경설정(Z3020M1)에서 연장가능=N인 사용자는 연장하지 못하지만, 만료 1분 전에는 알림을 줌(<확인>만 있고 누르면 닫기만 함).
    //  - 직접 모래시계를 눌렀을 때는 연장이 허용되지 않는다는 안내를 보여줌
    if (getUserInfo()?.sessExtYn === 'N') {
      await confirmMsgBox({
        title: ddKey('lblConfirm') || '확인',
        msgText: manual ? (ddKey('msgSessionExtendNotAllowed') || '세션 연장이 허용되지 않은 사용자입니다. 만료 후 다시 로그인하세요.') : (ddKey('msgSessionExpireSoon') || '세션이 곧 만료됩니다.'),
        showBtn: 'OK',
        msgType: 'info',
      });
      dismissExtendPrompt();
      return;
    }
    const confirmed = await confirmMsgBox({
      title: ddKey('lblConfirm') || '확인',
      msgText: ddKey('msgConfirmExtendSession') || '세션이 곧 만료됩니다. 연장하시겠습니까?',
      showBtn: 'YesNo',
    });
    if (!confirmed) {
      dismissExtendPrompt();
      return;
    }
    try {
      const loginUserInfo = getUserInfo();
      const res = await axios.post(`${API_BASE_URL}/com/apiUserToken`, { langCd: language, userId: loginUserInfo?.userId });
      const newExpiresAt = res?.data?.tokenExpiresAt;
      if (newExpiresAt) {
        extendTokenExpiry(newExpiresAt);
      } else {
        dismissExtendPrompt();
      }
    } catch (error) {
      dismissExtendPrompt();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ddKey, language]);

  // 남은시간이 1분 이하가 되면 연장 여부 확인창을 자동으로 띄움
  useEffect(() => {
    if (!showExtendPrompt) return;
    handleExtendSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExtendPrompt]);

  // 모래시계 아이콘 클릭 -> 수동으로 세션 연장 확인창 열기
  const handleTimerIconClick = useCallback(() => {
    handleExtendSession(true);
  }, [handleExtendSession]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  화면정의
  //		. 상단영역 : Header 영역
  //		. 메인 영역: Main 영역
  //		  . 좌측영역 : MainMenu 영역
  //		  . 중앙영역 : MainContent 영역
  //		  . 우측영역 : SubSidebar 영역
  //		. 하단부영역 : Foo
  // ter 영역
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 헤더 사용자 아이콘에 표시할 이름 첫 글자(한글/영문 모두, 영문은 대문자) - 프로필 이름이 있으면 그것, 없으면 로그인 사용자명
  const userInitial = Array.from(String(profileData?.user_nm || userInfo?.userName || '').trim())[0]?.toUpperCase() || '';

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* 헤더 영역 */}
      <header 
        className="header-bar"
        onClick={() => {
          setShowUserDropdown(false);
          setShowCtrlTabsDropdown(false);
          setShowNoticeDropdown(false);
        }}
      >
        {/* header-title과 메가메뉴를 하나의 좌측 그룹으로 묶어야 header-bar의 justify-content:space-between이
            셋을 고르게 분산시키지 않고, 로고 바로 옆에 메가메뉴가 붙어있게 됨(header-right는 계속 맨 끝) */}
        <div className="header-left">
          <div className="header-title">
        <i className="fas fa-cubes brand-icon"></i>
        <span className="brand-text" ref={brandTextRef}>{solutionTitle}</span>
          </div>
          {/* 상단 메가메뉴 - menuStyle==='topmenu'일 때만 로고 우측에 노출. 메인메뉴 목록만 보여주고
              (서브메뉴는 여기 없음 - 기존 서브사이드바가 그대로 담당), 클릭 시 좌측 메인사이드바 아이콘
              클릭과 완전히 동일하게 handleMainMenuClick을 그대로 호출함. */}
          {menuStyle === 'topmenu' && (
            <div className="mega-menu-wrap" ref={megaMenuRef} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="mega-menu-trigger"
                onClick={() => setShowMegaMenu(prev => !prev)}
              >
                {/* 헤더(컨테이너)가 좁아지면 mainFrame.css의 반응형 규칙이 mega-menu-trigger-label과
                    chevron을 숨기고 이 아이콘만 남김(로고 축소와 동일한 방식) */}
                <i className="fas fa-grip mega-menu-trigger-icon"></i>
                <span className="mega-menu-trigger-label">{ddKey('lblAllMenu') || ''}</span>
                <i className={`fas fa-chevron-${showMegaMenu ? 'up' : 'down'} mega-menu-trigger-chevron`}></i>
              </button>
              {showMegaMenu && (
                <div className="mega-menu-panel">
                  {/* 사이트메뉴전환(사이드바/상단메가메뉴) - 원래 헤더 우측 아이콘 버튼이었던 것을 이곳(패널 좌상단)으로 이동.
                      메가메뉴는 menuStyle==='topmenu'일 때만 렌더링되므로, 여기서는 항상 "사이드바로 전환"만 표시하면 됨. */}
                  <div className="mega-menu-toolbar">
                    <button
                      type="button"
                      className="mega-menu-toolbar-btn"
                      title={ddKey('lblMenuStyleSidebar') || '사이드바로 전환'}
                      onClick={() => {
                        handleMenuStyleToggle();
                        setShowMegaMenu(false);
                      }}
                    >
                      <i className="fas fa-bars"></i>
                    </button>
                    {/* 홈 - 상단 바로가기 줄에서 이 툴바(사이드바로 전환 버튼 옆)로 올림 */}
                    <button
                      type="button"
                      className="mega-menu-toolbar-btn"
                      title={ddKey('Home') || '홈'}
                      onClick={() => {
                        handleMainMenuClick('Home', false, '');
                        setShowMegaMenu(false);
                      }}
                    >
                      <i className="fas fa-house"></i>
                    </button>
                  </div>
                  {/* 상단 바로가기 - 공지사항/일정관리/1:1상담톡/DD생성(홈은 위 툴바로 이동). 공지사항·일정관리·상담톡은 사용자 메뉴에 있을 때만 노출(권한이 없으면 숨김).
                      나머지 메뉴는 그 아래 그리드에 그대로 유지됨. (2026-09-20: 즐겨찾기 제거, 일정관리/상담톡 추가) */}
                  <div className="mega-menu-quick-row">
                    {noticeMenu && (
                      <button
                        type="button"
                        className="mega-menu-quick-item"
                        onClick={() => {
                          setShowMegaMenu(false);
                          openNoticeTab();
                        }}
                      >
                        <i className="fas fa-bullhorn"></i>
                        <span>{ddKey('lblNoticeAlertTitle') || '공지사항'}</span>
                      </button>
                    )}
                    {scheduleMenu && (
                      <button
                        type="button"
                        className="mega-menu-quick-item"
                        onClick={() => {
                          setShowMegaMenu(false);
                          openScheduleTab();
                        }}
                      >
                        <i className="fas fa-calendar-alt"></i>
                        <span>{ddKey(scheduleMenu.item.itemKey || '') || scheduleMenu.item.itemName || '일정관리'}</span>
                      </button>
                    )}
                    {chatMenu && (
                      <button
                        type="button"
                        className="mega-menu-quick-item"
                        onClick={() => {
                          setShowMegaMenu(false);
                          openChatTab();
                        }}
                      >
                        <i className="fas fa-comments"></i>
                        <span>{ddKey(chatMenu.item.itemKey || '') || chatMenu.item.itemName || '1:1상담톡'}</span>
                      </button>
                    )}
                    {/* DD생성 - 원래 사용자영역 드롭다운(user-panel-actions) 안에 있던 것을 이곳으로 이동. 사용자환경설정(Z3020M1)의 DD생성이 체크된 사용자에게만 노출 */}
                    {userInfo?.ddCrtYn === 'Y' && (
                      <button type="button" className="mega-menu-quick-item" onClick={handleGenDD}>
                        <i className="fas fa-database"></i>
                        <span>{ddKey('btnGenDD') || 'DD 생성'}</span>
                      </button>
                    )}
                  </div>
                  <div className="mega-menu-main-grid">
                    {/* 공지사항(NOTICE)/일정관리(SCHEDULE)는 위 상단 바로가기에 이미 있으므로 하위메뉴 없는 단독 메인메뉴여도 타일에서 제외 */}
                    {mainMenuData.filter(item => item.key !== 'Home' && item.linkKey !== 'NOTICE' && item.linkKey !== 'SCHEDULE').map(item => (
                      <button
                        key={item.key}
                        type="button"
                        className={`mega-menu-tile ${activeMainMenu === item.key ? 'active' : ''}`}
                        onClick={() => {
                          handleMainMenuClick(item.key, item.hasSubMenu, item.url);
                          setShowMegaMenu(false);
                        }}
                      >
                        <i className={item.icon}></i>
                        <span>{ddKey(item.key) || item.title || item.key}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mega-menu-footer">
                    {ddKey('lblMegaMenuFooter') || `스마트한 업무 환경, ${solutionTitle}에서 시작하세요.`}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* 회사명 워터마크 - 지금 접속한 회사(테넌트)를 항상 은은하게 표시. 메가메뉴 스타일(topmenu)이든 사이드바
              스타일이든 로고 다음(메가메뉴가 있으면 그 다음) 자리에 오도록 header-left의 마지막 자식으로 둠.
              클릭/드래그에 끼어들지 않도록 pointer-events:none */}
          {userInfo?.coNm && <div className="header-watermark" aria-hidden="true">{userInfo.coNm}</div>}
        </div>
        {/* 메뉴 검색 - 메뉴명/메뉴ID를 입력하면 자동완성 목록이 뜨고, 선택(클릭/Enter)하면 해당 메뉴를 호출. 좁은 화면(모바일)에서는 숨김 */}
        <div className="header-search" onClick={(e) => e.stopPropagation()}>
          <div className="header-search-box" ref={menuSearchRef}>
            <input
              className="header-search-input"
              type="text"
              value={menuSearch}
              onChange={e => { setMenuSearch(e.target.value); setMenuSearchOpen(true); setMenuSearchIdx(-1); }}
              onFocus={() => { if (menuSearch.trim()) setMenuSearchOpen(true); }}
              onKeyDown={e => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setMenuSearchOpen(true);
                  setMenuSearchIdx(i => (menuSearchResults.length === 0 ? -1 : (i + 1) % menuSearchResults.length));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setMenuSearchIdx(i => (menuSearchResults.length === 0 ? -1 : (i <= 0 ? menuSearchResults.length - 1 : i - 1)));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  runMenuSearch();
                } else if (e.key === 'Escape') {
                  setMenuSearchOpen(false);
                  setMenuSearchIdx(-1);
                }
              }}
              placeholder={ddKey('lblMenuIdSearch') || '메뉴명/메뉴ID 검색 (예: K1001M1)'}
              aria-label={ddKey('lblMenuIdSearch') || '메뉴명/메뉴ID 검색'}
              aria-autocomplete="list"
              aria-expanded={menuSearchOpen && menuSearchResults.length > 0}
              autoComplete="off"
              spellCheck={false}
            />
            <button type="button" className="header-search-btn" onClick={runMenuSearch} title={ddKey('lblMenuIdSearchGo') || '메뉴 호출'} aria-label={ddKey('lblMenuIdSearchGo') || '메뉴 호출'}>
              <i className="fas fa-search"></i>
            </button>
            {menuSearchOpen && menuSearch.trim() && (
              <ul className="header-search-list" role="listbox">
                {menuSearchResults.length === 0 ? (
                  <li className="header-search-empty">{ddKey('msgMenuSearchNoResult') || '일치하는 메뉴가 없습니다.'}</li>
                ) : menuSearchResults.map((m, i) => (
                  <li
                    key={`${m.hit.kind}-${m.id}`}
                    role="option"
                    aria-selected={i === menuSearchIdx}
                    className={`header-search-item${i === menuSearchIdx ? ' active' : ''}`}
                    onMouseDown={(e) => e.preventDefault() /* 입력창 포커스 유지 */}
                    onMouseEnter={() => setMenuSearchIdx(i)}
                    onClick={() => openMenuHit(m.hit)}
                  >
                    <i className={`${m.hit.kind === 'main' ? m.hit.item.icon : (m.hit.item.icon || 'fas fa-file-alt')} header-search-item-icon`}></i>
                    <span className="header-search-item-main">
                      <span className="header-search-item-name">{m.name}</span>
                      {m.path && <span className="header-search-item-path">{m.path}</span>}
                    </span>
                    <code className="header-search-item-id">{m.id}</code>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="header-right">
            {/* 탭제어 영역 - 모래시계(남은시간) 좌측으로 이동 */}
            {openTabs.length > 1 || (openTabs.length === 1 && openTabs[0].id !== 'Home') ? (
              <div className="ctrlTabs-dropdown" ref={ctrlTabsDropdownRef}>
                  <button
                  onClick={(e) => {
                      e.stopPropagation(); // 이벤트 전파 중단
                      setShowUserDropdown(false); // 사용자 드롭다운 닫기
                      setShowNoticeDropdown(false); // 공지사항 드롭다운 닫기
                      setShowCtrlTabsDropdown(!showCtrlTabsDropdown);
                  }}
                  className="ctrlTabs-dropdown-btn flex items-center gap-2 px-3 py-2 bg-transparent border-none text-gray-600 hover:text-gray-800 cursor-pointer"
                  id="ctrlTabsDropdownBtn"
                  >
                      <i className="fas fa-window-restore"></i>
                      <span className="ctrlTabs-name font-medium" id="ctrlTabsNameText">{ddKey('lblCtrlTabsName') || '탭'}</span>
                      <i className="fas fa-caret-down"></i>
                  </button>
                  {showCtrlTabsDropdown && (
                  <div
                    className="ctrlTabs-dropdown-menu absolute right-0 top-full mt-2 min-w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[70] py-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                      {renderCtrlTabsMenuItems()}
                  </div>
                  )}
              </div>
            ) : null}
            {/* 세션영역 : 토큰 만료시각/남은시간 표시 */}
            {tokenExpiry && (
              <Tooltip title={ddKey('lblExtendSessionTooltip') || '클릭하면 세션을 연장할 수 있습니다'}>
                <span
                  className="session-time-wrap text-xs text-gray-500 mr-4"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTimerIconClick();
                  }}
                >
                  {/* 토큰 만료: {new Date(tokenExpiry).toLocaleString()} */}
                  {/* 헤더가 좁아지면 mainFrame.css의 반응형 규칙이 이 텍스트만 숨기고 모래시계 아이콘만 남김 */}
                  {/* 세션 남은시간이 5분 이하이면 빨간색으로 표시 */}
                  {(() => {
                    const [hh, mm, ss] = (tokenRemainHMS || '').split(':').map(Number);
                    const low = tokenRemainHMS !== null && (hh * 3600 + mm * 60 + ss) <= 300;
                    const st = low ? { color: '#dc2626', fontWeight: 700 } : undefined;
                    return (<>
                      <span className="session-time-text" style={st}>{tokenRemainHMS !== null ? tokenRemainHMS : '-'}</span>
                      <i className="fas fa-hourglass-half" style={st}></i>
                    </>);
                  })()}
                </span>
              </Tooltip>
            )}
            {/* 공지사항 알림 영역 - 사용자영역 드롭다운과 동일한 방식(아이콘 클릭 -> 평면 목록 패널 토글) */}
            <div className="notice-alert" ref={noticeDropdownRef}>
              <Tooltip title={ddKey('lblNoticeAlertTooltip') || '공지사항'}>
                <button
                  type="button"
                  className="notice-alert-btn header-icon-circle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserDropdown(false);
                    setShowCtrlTabsDropdown(false);
                    setShowNoticeDropdown(!showNoticeDropdown);
                    // 패널을 열 때마다 최신 읽음상태를 다시 조회 - 5분 주기 자동조회 사이에 다른 탭(X1002Q1
                    // 목록/상세)에서 읽음처리한 내용이 있어도 여기서 바로 반영되게 함.
                    if (!showNoticeDropdown) checkNewNotices();
                  }}
                >
                  <i className="fas fa-bell"></i>
                  {hasNewNotice && <span className="notice-alert-badge" />}
                </button>
              </Tooltip>
              {showNoticeDropdown && (
                <div className="notice-alert-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="notice-alert-header">
                    <span
                      className="notice-alert-title-link"
                      onClick={() => {
                        setShowNoticeDropdown(false);
                        openNoticeTab();
                      }}
                    >
                      <i className="fas fa-bell" style={{ color: '#4f46e5', marginRight: 6 }} />
                      {ddKey('lblNoticeAlertTitle') || '공지사항'}
                    </span>
                    <span className="notice-alert-readall" onClick={handleNoticeMarkAllRead}>
                      {ddKey('btnNoticeMarkAllRead') || '모두 읽음'}
                    </span>
                  </div>
                  <div className="notice-alert-list">
                    {notices.length === 0 ? (
                      <div className="notice-alert-empty">{ddKey('msgNoNewNotice') || '새로운 공지사항이 없습니다.'}</div>
                    ) : (
                      notices.slice(0, 5).map(notice => (
                        <div key={notice.id} className="notice-alert-item" onClick={() => handleNoticeItemClick(notice)}>
                          <span className={`notice-alert-avatar ${notice.readYn === 'N' ? 'is-unread' : ''}`}>
                            <i className={notice.readYn === 'N' ? 'fas fa-envelope' : 'fas fa-envelope-open'}></i>
                          </span>
                          <div className="notice-alert-content">
                            <div className={`notice-alert-title ${notice.isImportant ? '' : 'is-normal'}`}>{notice.title}</div>
                            <div className="notice-alert-message">{notice.message}</div>
                            <div className="notice-alert-time">
                              <span className="notice-alert-expire">
                                {notice.expireDt ? `${ddKey('lblExpireDtShort') || '만료'} ${notice.expireDt}` : ''}
                              </span>
                              <span className="notice-alert-regdt">{notice.regDt}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* 사용자 영역 - 프로필아이콘/캐럿 둘 다 같은 통합패널(프로필정보+개인정보변경/비밀번호변경/DD생성/로그아웃)을
            토글함(메가메뉴와 동일한 앵커형 드롭다운 스타일 - user-panel/user-panel-* 클래스는 mega-menu-panel 참고) */}
            <div className="user-dropdown" ref={userDropdownRef}>
                <div className="user-dropdown-btn" id="userDropdownBtn">
                    {/* 사용자 아이콘 - 이름의 첫 글자를 회색 원 안에 흰색으로 표시(이름이 없으면 기본 사람 아이콘) */}
                    <button
                        type="button"
                        onClick={handleToggleUserPanel}
                        className={`user-dropdown-btn header-icon-circle${userInitial ? ' user-initial-avatar' : ''}`}
                        title={ddKey('lblViewProfile') || '개인정보 보기'}
                    >
                        {userInitial ? <span className="user-initial-text">{userInitial}</span> : <i className="fas fa-user"></i>}
                        {userKindNm && <span className={`user-icon-kind-badge k-${userKindCd}`} aria-hidden="true">{Array.from(userKindNm)[0]}</span>}
                    </button>
                </div>
                {showUserDropdown && (
                <div className="user-panel" onClick={(e) => e.stopPropagation()}>
                    {profileLoading ? (
                        <div className="user-panel-loading"><i className="fas fa-spinner fa-spin"></i></div>
                    ) : (
                        <>
                            {/* 회사명(항상) + 회사코드(사용자환경설정 [서버 접속 정보 보기]의 회사코드가 체크된 경우만) */}
                            {(userInfo?.coNm || userInfo?.serverInfo?.coCd !== undefined) && (
                                <div className="user-panel-company">
                                    <i className="fas fa-building"></i>
                                    <span className="user-panel-company-name">{userInfo?.coNm || '-'}</span>
                                    {userInfo?.serverInfo?.coCd !== undefined && <span className="user-panel-company-code">{userInfo.serverInfo.coCd || '-'}</span>}
                                </div>
                            )}
                            <div className="user-panel-profile">
                                <div className="user-panel-avatar">
                                    <i className="fas fa-user"></i>
                                    {userKindNm && <span className={`user-panel-kind-badge k-${userKindCd}`}>{Array.from(userKindNm)[0]}</span>}
                                </div>
                                <div className="user-panel-profile-text">
                                    {/* 1줄 : 사용자명 / 2줄 : 사용자ID(왼쪽) + 사용자유형 배지(오른쪽) */}
                                    <div className="user-panel-name" title={profileData?.user_nm || userInfo?.userName || ''}>{profileData?.user_nm || userInfo?.userName || ''}</div>
                                    <div className="user-panel-id-row">
                                        <span className="user-panel-userid">{userInfo?.userId || ''}</span>
                                        {userKindNm && <span className={`user-panel-kind k-${userKindCd}`}>{userKindNm}</span>}
                                    </div>
                                </div>
                            </div>
                            {profileError && (
                                <div className="user-panel-info-row">
                                    <span style={{ color: '#dc2626' }}><i className="fas fa-triangle-exclamation" style={{ marginRight: 6, color: '#dc2626' }}></i>{profileError}</span>
                                </div>
                            )}
                            <div className="user-panel-dept" title={profileData?.org_path || ''}>
                                <i className="fas fa-sitemap"></i>
                                <span>{profileData?.org_path || '-'}</span>
                            </div>
                            <hr className="my-1 border-gray-200" />
                            <div className="user-panel-info-row">
                                <span><i className="fas fa-envelope" style={{ marginRight: 6, opacity: 0.7 }}></i>{ddKey('lblEmail') || 'E-Mail'}</span>
                                <span>{profileData?.email || '-'}</span>
                            </div>
                            <div className="user-panel-info-row">
                                <span><i className="fas fa-mobile-screen-button" style={{ marginRight: 6, opacity: 0.7 }}></i>{ddKey('lblMobile') || '휴대전화'}</span>
                                <span>{profileData?.hp_no || '-'}</span>
                            </div>
                            <hr className="my-1 border-gray-200" />
                            <div className="user-panel-actions">
                                <button type="button" onClick={handleProfileEdit} className="user-panel-action-btn">
                                    <i className="fas fa-id-card"></i>
                                    <span>{ddKey('btnProfileEdit') || '개인정보변경'}</span>
                                </button>
                                <button type="button" onClick={handlePasswordEdit} className="user-panel-action-btn">
                                    <i className="fas fa-key"></i>
                                    <span>{ddKey('btnPasswordEdit') || '비밀번호변경'}</span>
                                </button>
                            </div>
                            <hr className="my-1 border-gray-200" />
                            <button
                                type="button"
                                onClick={() => {
                                    handleLogout();
                                    setShowUserDropdown(false);
                                }}
                                className="user-panel-logout-btn"
                            >
                                <i className="fas fa-sign-out-alt"></i>
                                <span>{ddKey('btnLogOut') || '로그아웃'}</span>
                            </button>
                            {/* 서버 접속 정보 - 사용자환경설정(Z3020M1)의 [서버 접속 정보 보기]에서 체크된 항목만 표시(서버가 체크된 항목만 내려줌) */}
                            {(['appIp', 'dbIp', 'dbNm'] as const).some(k => userInfo?.serverInfo?.[k] !== undefined) && (<>
                                <hr className="my-1 border-gray-200" />
                                <div className="user-panel-server-info">
                                {([
                                    ['appIp', 'fa-server', ddKey('lblAppIp') || 'App IP'],
                                    ['dbIp', 'fa-network-wired', ddKey('lblDbIp') || 'DB IP'],
                                    ['dbNm', 'fa-database', ddKey('lblDbNm') || 'DB명'],
                                ] as const).filter(([k]) => userInfo?.serverInfo?.[k] !== undefined).map(([k, icon, label]) => (
                                    <div className="user-panel-info-row" key={k}>
                                        <span><i className={`fas ${icon}`} style={{ marginRight: 6, opacity: 0.7 }}></i>{label}</span>
                                        <span>{userInfo?.serverInfo?.[k] || '-'}</span>
                                    </div>
                                ))}
                                </div>
                            </>)}
                        </>
                    )}
                </div>
                )}
            </div>
            <BackendStatusBadge clickable hideLabel solutionTitle={solutionTitle} />
            {/* 2026-09-20: 헤더 우측의 "상단메뉴전환" 버튼 제거 - 좌측 사이드바 [홈] 자리(handleSwitchToMegaMenu)가
                사이드바 -> 상단메가메뉴 전환 경로를 대신 제공하므로 더 이상 막다른 길이 아님. */}
            <div onClick={(e) => e.stopPropagation()}>
                <select
                    id="langSelect"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-40 p-2.5"
                    value={language}
                    aria-label={ddKey('language') || 'Language'}
                    onChange={(e) => {
                      const newLanguage = e.target.value as any;
                      getLanguage(newLanguage);
                      // 현재 사용자 정보가 있으면 언어 정보도 업데이트
                      if (userInfo) {
                        const updatedUserInfo = {
                          ...userInfo,
                          language: newLanguage
                        };
                        setUserInfo(updatedUserInfo); // 로컬 상태 업데이트
                        setLocalStorage(updatedUserInfo); // localStorage에 저장
                      }
                    }}
                   >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.flag ? `${lang.flag} ` : ''}{lang.label}</option>
                  ))}
                </select>
            </div>
        </div>
      </header>
      {/* 앱컨테이너영역 */}
      <div className={`app-container ${menuStyle === 'topmenu' ? 'topmenu-mode' : `sidebar-mode${isSidebarCollapsed ? '' : ' sidebar-expanded'}`}`}>
          {/* 서브사이드바 열기/닫기 버튼 - 기기/화면폭/메뉴표시방식과 무관하게 항상 표시. 좌측 메인사이드바가 있는 모드는
              그 폭(접힘 60px/펼침 120px)만큼 오른쪽에서 시작함 */}
          {/* 고정(핀) 상태에서 메뉴가 열려 있으면 닫기 화살표는 숨김 - 닫기는 헤더의 ≡ 아이콘으로만 */}
          {!(showSubSidebar && subSidebarPinned) && (
            <button
              type="button"
              className="mobile-submenu-toggle"
              style={showSubSidebar ? { left: `calc(var(--submenu-toggle-base, 0px) + min(${subSidebarWidth}px, 85vw))` } : undefined}
              onClick={handleMobileSubSidebarToggle}
              title={showSubSidebar ? (ddKey('lblCloseSubmenu') || '서브메뉴 닫기') : (ddKey('lblOpenSubmenu') || '서브메뉴 열기')}
            >
              <i className={`fas ${showSubSidebar ? 'fa-angle-left' : 'fa-angle-right'}`}></i>
            </button>
          )}
          {/* 메인 사이드바 - menuStyle==='topmenu'일 때는 상단 메가메뉴가 이 역할을 대신하므로 숨김 */}
          {menuStyle === 'sidebar' && (
          <nav
            className={`main-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}
          >
            {/* 메뉴 항목이 많아지거나 브라우저가 축소돼서 목록이 다 안 보일 때, 스크롤바 대신 상/하
                버튼(항목 1개 높이만큼 이동)으로 넘겨볼 수 있게 함 - 넘칠 때만 표시(useSidebarScrollButtons) */}
            {showSidebarScrollUp && (
              <button
                type="button"
                onClick={() => scrollSidebarMenu('up')}
                className="sidebar-scroll-btn up"
                title={ddKey('lblScrollSidebarUp') || '위로'}
              >
                <i className="fas fa-chevron-up"></i>
              </button>
            )}
            {/* 메인 메뉴리스트 - [홈] 자리는 상단메가메뉴 전환 버튼으로 대체(아래 item.key==='Home' 분기 참고) */}
            <ul className="main-sidebar-menu" id="mainMenu">
              {mainMenuData.map(item => {
                const isHomeSlot = item.key === 'Home';
                const label = isHomeSlot ? (ddKey('lblAllMenu') || '') : (ddKey(item.key) || item.title || item.key);
                return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isHomeSlot) {
                        handleSwitchToMegaMenu();
                        return;
                      }
                      handleMainMenuClick(item.key, item.hasSubMenu, item.url);
                    }}
                    className={activeMainMenu === item.key ? 'active' : ''}
                    title={label}
                  >
                    {/* 홈 아이콘(fa-house)은 리마크 처리 - 이 자리는 이제 상단메가메뉴 전환 버튼이라 다른 아이콘을 씀.
                        홈 기능 자체는 메가메뉴 퀵메뉴의 [홈] 버튼으로 계속 제공됨. */}
                    {/* {isHomeSlot && <i className={item.icon}></i>} */}
                    <i className={isHomeSlot ? 'fas fa-grip' : item.icon}></i>
                    <span
                      className="main-sidebar-menu-text"
                    >
                      {label}
                    </span>
                    <span
                      className="main-sidebar-menu-initial"
                    >
                      {label.substring(0, 2)}
                    </span>
                  </button>
                </li>
                );
              })}
            </ul>
            {showSidebarScrollDown && (
              <button
                type="button"
                onClick={() => scrollSidebarMenu('down')}
                className="sidebar-scroll-btn down"
                title={ddKey('lblScrollSidebarDown') || '아래로'}
              >
                <i className="fas fa-chevron-down"></i>
              </button>
            )}

            {/* 사이드바 확장/축소 버튼 */}
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              title={isSidebarCollapsed ? (ddKey('lblSidebarExpand') || '확장') : (ddKey('lblSidebarCollapse') || '축소')}
              className="sidebar-toggle-btn"
            >
              <i className={`fas ${isSidebarCollapsed ? 'fa-angle-double-right' : 'fa-angle-double-left'}`}></i>
            </button>
          </nav>
          )}

          {/* 서브 사이드바 - menuStyle과 무관하게 항상 기존 방식 그대로 동작(메가메뉴는 좌측 메인메뉴
              아이콘 클릭을 대신할 뿐, 서브메뉴 영역은 건드리지 않음) */}
          <nav
            className={`sub-sidebar ${!showSubSidebar ? 'hidden' : ''}${isSubSidebarDragging ? ' no-transition' : ''}`}
            id="subSidebar"
            // hidden 상태(닫힘)는 CSS(.sub-sidebar.hidden{width:0})가 담당해야 하므로, 그때는 인라인 width를
            // 아예 안 줌(인라인 style이 클래스보다 우선순위가 높아 width:0을 덮어써버리기 때문).
            style={showSubSidebar ? { width: subSidebarWidth } : undefined}
          >
            {showSubSidebar && !(activeMainMenu && subMenuData[activeMainMenu]) && (
              <>
                <div className="sub-sidebar-header">
                  <span>
                    <i className="fas fa-folder-open"></i>
                    <span>{ddKey('lblMenu') || '메뉴'}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="sub-sidebar-close-btn sub-sidebar-pin-btn"
                      onClick={toggleSubSidebarPinned}
                      title={subSidebarPinned ? (ddKey('lblSubSidebarPinned') || '고정됨 (클릭하면 미고정)') : (ddKey('lblSubSidebarUnpinned') || '미고정 (클릭하면 고정)')}
                      aria-pressed={subSidebarPinned}
                    >
                      <i className={`fas fa-thumbtack${subSidebarPinned ? '' : ' unpinned'}`}></i>
                    </button>
                    <button
                      type="button"
                      title={ddKey('lblCloseSubmenu') || '서브메뉴 닫기'}
                      onClick={() => setShowSubSidebar(false)}
                      className="sub-sidebar-close-btn"
                    >
                      <i className="fas fa-bars"></i>
                    </button>
                  </span>
                </div>
                <div className="sub-sidebar-empty">{ddKey('msgNoMenu') || '사용 가능한 메뉴가 없습니다.'}</div>
              </>
            )}
            {showSubSidebar && activeMainMenu && subMenuData[activeMainMenu] && (
              <>
                {/* 서브 메뉴 헤더 */}
                <div className="sub-sidebar-header">
                  <span>
                    <i className={subMenuData[activeMainMenu].icon}></i>
                    <span title={ddKey(activeMainMenu) || subMenuData[activeMainMenu].titleName || activeMainMenu}>{ddKey(activeMainMenu) || subMenuData[activeMainMenu].titleName || activeMainMenu}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="sub-sidebar-close-btn sub-sidebar-pin-btn"
                      onClick={toggleSubSidebarPinned}
                      title={subSidebarPinned ? (ddKey('lblSubSidebarPinned') || '고정됨 (클릭하면 미고정)') : (ddKey('lblSubSidebarUnpinned') || '미고정 (클릭하면 고정)')}
                      aria-pressed={subSidebarPinned}
                    >
                      <i className={`fas fa-thumbtack${subSidebarPinned ? '' : ' unpinned'}`}></i>
                    </button>
                    <button
                      type="button"
                      title={ddKey('lblCloseSubmenu') || '서브메뉴 닫기'}
                      onClick={() => setShowSubSidebar(false)}
                      className="sub-sidebar-close-btn"
                    >
                      <i className="fas fa-bars"></i>
                    </button>
                  </span>
                </div>
                {/* 서브 메뉴리스트 */}
                <ul className="sub-sidebar-menu">
                  {subMenuData[activeMainMenu].items.map(item => renderSubMenuItem(item))}
                </ul>
              </>
            )}
          </nav>

          {/* 서브사이드바 폭 조절 핸들 - 닫혀있을 때는 감출 필요 없음(잡을 게 없어 클릭해도 아무 일도 안 남지만,
              폭 0인 사이드바 바로 옆이라 시각적으로도 거의 안 보임). 좁은 화면(모바일)에서는 CSS로 숨김. */}
          {showSubSidebar && (
            <div
              className="sub-sidebar-resizer"
              onPointerDown={handleSubSidebarResizeStart}
              title={ddKey('lblSubSidebarResize') || '드래그해서 폭 조절'}
            />
          )}

          {/* 메인 컨텐츠 영역 */}
          <main className={`main-content ${!isSidebarCollapsed ? 'sidebar-expanded' : ''}`}>
            {/* 탭 영역 */}
            <div className="mainContentTopBar">
              {/* 탭 좌측 스크롤 버튼 */}
              {showTabScrollLeft && (
                <button
                  onClick={scrollTabsLeft}
                  className="tab-scroll-btn left"
                  title={ddKey('lblScrollTabsLeft') || '탭 왼쪽으로 스크롤'}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
              )}
              
              {/* 탭 우측 스크롤 버튼 */}
              {showTabScrollRight && (
                <button
                  onClick={scrollTabsRight}
                  className="tab-scroll-btn right"
                  title={ddKey('lblScrollTabsRight') || '탭 오른쪽으로 스크롤'}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              )}
              
              {/* 오픈되는 탭 영역 */}
              <div 
                className={`mainContentOpenTabs ${showTabScrollLeft ? 'pad-left-35' : ''} ${showTabScrollRight ? 'pad-right-35' : ''}`} 
                id="mainContentOpenTabs"
              >
                <div className="tabs-inline">
                  {openTabs.map((tab, index) => (
                    <button
                      key={tab.id}
                      className={`main-tab-btn ${tab.active ? 'active' : ''} ${tab.id === 'Home' ? 'home-tab' : ''}${dragTabId === tab.id ? ' dragging' : ''}${dragOverTabId === tab.id && dragTabId !== tab.id ? ' drag-over' : ''}`}
                      onClick={() => activateTab(tab.id)}
                      onContextMenu={(e) => handleTabContextMenu(e, tab)}
                      title={tab.title}
                      draggable={tab.id !== 'Home'}
                      onDragStart={(e) => {
                        if (tab.id === 'Home') {
                          e.preventDefault();
                          return;
                        }
                        dragFromIndexRef.current = index;
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', index.toString());
                        // 드래그 이미지가 만들어진 다음에 드래그 중 스타일(테두리/축소/회전)을 적용 - dragstart 안에서 바로 레이아웃을 바꾸면 브라우저에 따라 드래그가 취소됨
                        window.setTimeout(() => setDragTabId(tab.id), 0);
                      }}
                      onDragEnd={resetTabDrag}
                      onDragOver={(e) => {
                        // 이 화면의 탭 이동 드래그만 받음(외부 파일/텍스트 드래그는 무시), Home 탭 위치로는 드롭 불가
                        if (dragFromIndexRef.current < 0 || tab.id === 'Home' || index === 0) {
                          return;
                        }
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverTabId !== tab.id) setDragOverTabId(tab.id);
                      }}
                      onDragLeave={(e) => {
                        // 탭 안쪽 요소(글자/아이콘)로 옮겨가는 경우는 나간 것이 아님
                        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                        setDragOverTabId(prev => (prev === tab.id ? null : prev));
                      }}
                      onDrop={(e) => {
                        // Home 탭 위치로는 드롭 불가
                        if (dragFromIndexRef.current < 0 || tab.id === 'Home' || index === 0) {
                          e.preventDefault();
                          resetTabDrag();
                          return;
                        }
                        e.preventDefault();
                        const fromIndex = dragFromIndexRef.current >= 0 ? dragFromIndexRef.current : parseInt(e.dataTransfer.getData('text/plain'), 10);
                        const toIndex = index;
                        resetTabDrag();
                        if (!Number.isNaN(fromIndex)) moveTab(fromIndex, toIndex);
                      }}
                    >
                      <span>
                        {tab.url === 'HomePage' ? (
                          <i className="fas fa-home w-4" onClick={(e) => handleTabIconClick(e, tab)}></i>
                        ) : tab.icon ? (
                          <i className={`${tab.icon} w-4`} onClick={(e) => handleTabIconClick(e, tab)}></i>
                        ) : ''}
                        {(tab.url === 'HomePage' || tab.icon) && '\u00A0\u00A0'}
                        {tab.title}
                      </span>
                      {tab.url !== 'HomePage' && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            closeTab(tab.id);
                          }}
                          className="tab-close"
                        >
                          <i className="fas fa-times w-4"></i> {/* 탭 닫기 아이콘 : x, &times;*/}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              {/* 활성 탭 우클릭 메뉴 - 헤더 "탭관리" 드롭다운과 같은 항목(renderCtrlTabsMenuItems)을 클릭 좌표에 고정 표시 */}
              {tabCtxMenu && (
                <div
                  ref={tabCtxMenuRef}
                  className="ctrlTabs-dropdown-menu min-w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[70] py-1"
                  style={{ position: 'fixed', top: tabCtxMenu.mouseY, left: tabCtxMenu.mouseX }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderCtrlTabsMenuItems()}
                </div>
              )}
              {/* 홈페이지를 제외, 현재 활성 탭의 메뉴 경로 표시 */}
              {openTabs.length > 0 && openTabs.find(tab => tab.active)?.url !== 'HomePage' && (
                <div className="mainContentLoc" id="mainContentLoc">
                  <div className="mainContentLoc-content">
                    {(() => {
                      const active = openTabs.find(tab => tab.active);
                      if (!active) return null;
                      // 현재 위치 브레드크럼 - 메인메뉴 > (그룹 >) 화면. 마지막(현재 화면)을 강조하고 프로그램 경로는 옆에 작은 태그로 표시.
                      const segs = (active.menuPath || active.title).split(' > ');
                      return (
                        <nav className="loc-breadcrumb" aria-label="현재 위치">
                          <ol>
                            {segs.map((seg, i) => (
                              <li key={i} className={i === segs.length - 1 ? 'current' : ''} aria-current={i === segs.length - 1 ? 'page' : undefined}>
                                {i > 0 && <i className="fas fa-chevron-right loc-bc-sep" aria-hidden="true"></i>}
                                <span>{seg}</span>
                              </li>
                            ))}
                          </ol>
                          {userInfo?.userKind === 'SA' && (
                            <code className="loc-bc-pgm" title={ddKey('lblProgramId') || '프로그램 경로'}>{active.url}</code>
                          )}
                        </nav>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            {/* 활성화되는 페이지 영역 */}
            <div className="mainContent" id="mainContent">
              {/* 맨 위로 가기 버튼 */}
              {showScrollToTop && (
                <button
                  onClick={scrollToTop}
                  className="scroll-btn to-top"
                  title={ddKey('lblScrollToTop') || '맨 위로 가기'}
                >
                  <i className="fas fa-chevron-up"></i>
                </button>
              )}
              
              {/* 맨 아래로 가기 버튼 */}
              {showScrollToBottom && (
                <button
                  onClick={scrollToBottom}
                  className="scroll-btn to-bottom"
                  title={ddKey('lblScrollToBottom') || '맨 아래로 가기'}
                >
                  <i className="fas fa-chevron-down"></i>
                </button>
              )}
              {openTabs.map(tab => {
                // 🏠 특별한 홈페이지 처리
                if (tab.url === 'HomePage') {                      
                  return (
                    <div
                      key={`${tab.id}-${tab.reloadKey ?? 0}`}
                      className={`tab-content react-content custom-scrollbar ${tab.active ? 'active' : 'inactive'}`}
                      onLoad={() => {
                        //console.log(`111111로드 완료: ${tab.title} (${tab.url})`);
                      }}
                    >
                      {homePgmId === null ? (
                        <DelayedLoading />
                      ) : (
                        <HomeRenderer pgmId={homePgmId} homeProps={{ scheduleEnabled: !!scheduleMenu, onOpenSchedule: handleOpenScheduleFromHome, onGoToSchedule: () => openScheduleTab(), onOpenNotice: handleQuickNoticeFromHome, onNewNotice: handleNewNoticeFromHome, onOpenMenu: openMenuByPgmId }} />
                      )}
                    </div>
                  );
                } 
                // 🔗 외부URL 메뉴(LINK_KEY='ADDTAB') - xGA 탭 안에 iframe으로 URL을 그대로 띄움(예외적 용도)
                else if (tab.linkKey === 'ADDTAB') {
                  return (
                    <div
                      key={`${tab.id}-${tab.reloadKey ?? 0}`}
                      className={`tab-content react-content custom-scrollbar ${tab.active ? 'active' : 'inactive'}`}>
                      <IframeTab url={tab.url} title={tab.title} />
                    </div>
                  );
                }
                // ⚛️ React 컴포넌트 동적 렌더링 (2단계: 현대화된 컴포넌트)
                else
                  {
                  return (
                    <div
                      key={`${tab.id}-${tab.reloadKey ?? 0}`}
                      className={`tab-content react-content custom-scrollbar ${tab.active ? 'active' : 'inactive'}`}>
                      <ComponentRenderer url={tab.url} paramData={tab.paramData} autoQuery={tab.autoQuery} menuId={tab.id} />
                    </div>
                  );
                }
              })}
            </div>
          </main>
      </div>
      {/* 사용자영역 > 비밀번호변경 팝업 - LoginPage의 강제 비밀번호변경과 동일 컴포넌트 재사용 */}
      <ChangePasswordModal
        open={pwdChangeOpen}
        userId={userInfo?.userId || ''}
        onClose={() => setPwdChangeOpen(false)}
        onSuccess={() => {
          setPwdChangeOpen(false);
          showMsgBox({ title: ddKey('lblAlertTitle') || '알림', msgText: ddKey('msgPwdChanged') || '비밀번호가 변경되었습니다.', msgType: 'success' });
        }}
      />
      {/* 공지사항 팝업 - X1001M1(공지사항등록)에서 팝업여부를 켜둔 활성 공지를 로그인 직후 폼로드 시
          자동으로 보여줌. 여러 건이면 한 모달 안에 목록형으로 전부 표시(스크롤). */}
      <Dialog open={showNoticePopup} onClose={handleNoticePopupClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-bell" style={{ color: '#4f46e5' }} />
            {ddKey('lblNoticeAlertTitle') || '공지사항'}
          </span>
          <IconButton size="small" onClick={handleNoticePopupClose} aria-label={ddKey('btnClose') || '닫기'}>
            <i className="fas fa-xmark" style={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        {/* pt:0 - DialogContent 자체의 상단 패딩이 있으면 sticky 오프셋이 그 패딩 안쪽에서 계산되어,
            스크롤을 지나간 이전 공지 내용이 그 패딩 틈으로 살짝 보이는 문제가 생김(sticky 제목 위쪽 공백).
            상단 여백은 컨테이너가 아니라 각 제목 줄 자체의 paddingTop으로 대신 줘서 완전히 붙게 함. */}
        <DialogContent dividers sx={{ maxHeight: '60vh', pt: 0, pb: 2 }}>   {/* pb : 마지막 공지 아래 여백(2026-10-29) */}
          {popupNotices.map((notice, idx) => (
            <div key={notice.id} style={{ marginBottom: idx < popupNotices.length - 1 ? 20 : 0, paddingBottom: idx < popupNotices.length - 1 ? 16 : 0, borderBottom: idx < popupNotices.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
              {/* position:sticky - 이 공지 항목(위 outer div)의 높이 범위 안에서만 상단에 붙어있다가,
                  이 항목이 스크롤로 다 지나가면 자연스럽게 다음 공지의 제목으로 교체됨(JS 없이 CSS만으로 동작) */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6,
                position: 'sticky', top: 0, zIndex: 1, background: '#f8f9fa',
                paddingTop: 8, paddingBottom: 6, boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
              }}>
                <span
                  style={{ fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                  title={ddKey('lblGoToNoticeDetail') || '공지사항 상세보기로 이동'}
                  onClick={() => { handleNoticePopupClose(); handleOpenNoticeFromHome(notice.id); }}
                >{notice.title}</span>
                <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0, marginLeft: 8 }}>{notice.regDt}</span>
              </div>
              <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notice.content) }} />
              {/* 첨부파일(X1001M1에서 등록) - X1002Q1 상세와 동일하게 본문 아래에 카드로 나열, 다운로드만 가능 */}
              {!!notice.files?.length && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                  {notice.files.map(f => {
                    const fi = getFileIcon(f.org_file_nm || '');
                    return (
                      <a key={f.file_seq} href={`${API_BASE_URL}/${f.file_path}`} target="_blank" rel="noreferrer" title={f.org_file_nm}
                        style={{ width: 108, flex: '0 0 auto', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 6px', textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>
                        <i className={`fas ${fi.icon}`} style={{ fontSize: 24, color: fi.color }}></i>
                        <div style={{ fontSize: 11, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.org_file_nm}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{formatFileSize(f.file_size || 0)}</div>
                      </a>
                    );
                  })}
                </div>
              )}
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox size="small"
                    checked={!!popupDismissToday[notice.id]}
                    onChange={e => setPopupDismissToday(prev => ({ ...prev, [notice.id]: e.target.checked }))} />
                }
                label={<span style={{ fontSize: 12, color: '#64748b' }}>{ddKey('btnDontShowToday') || '오늘 하루 다시 보지 않기'}</span>} />
            </div>
          ))}
        </DialogContent>
        <div style={{ height: 16, flex: '0 0 auto' }} />{/* 팝업 하단 여백 - 본문 아래 구분선이 팝업 바닥에 붙지 않게(2026-10-29) */}
      </Dialog>

      {/* 홈 알림 센터 위젯의 공지 미리보기 팝업 - 공지사항조회 탭으로 이동하지 않고 내용만 간단히 보여줌(2026-10-03) */}
      <Dialog open={!!quickNotice} onClose={handleQuickNoticeClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-bullhorn" style={{ color: '#4f46e5' }} />
            {ddKey('lblNoticeAlertTitle') || '공지사항'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={handleQuickNoticeMore} title={ddKey('lblGoToNoticeDetail') || '자세히보기'}>
              <i className="fas fa-external-link-alt" style={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" onClick={handleQuickNoticeClose} aria-label={ddKey('btnClose') || '닫기'}>
              <i className="fas fa-xmark" style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </DialogTitle>
        {quickNotice && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 24px 14px', borderBottom: '1px solid #e2e8f0' }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{quickNotice.title}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 8 }}>
              <ScopeBadge scopeTp={quickNotice.scopeTp} />
              {(() => {
                const d = (quickNotice.expireDt || '').slice(0, 10);
                if (!d || d.startsWith('1900')) return null; // 미설정/구자료 기본값(1900-01-01)은 무기한 - 표시 안 함(X1002Q1과 동일 관례)
                return (
                  <span title={ddKey('lblExpireDt') || '만료일'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: '#fef2f2', color: '#dc2626' }}>
                    <i className="fas fa-calendar-xmark"></i>{d}
                  </span>
                );
              })()}
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{quickNotice.regDt}</span>
            </span>
          </div>
        )}
        <DialogContent sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {quickNotice && (
            <>
              <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quickNotice.content) }} />
              {/* 첨부파일(X1001M1에서 등록) - X1002Q1 상세와 동일하게 본문 아래에 카드로 나열, 다운로드만 가능 */}
              {quickNoticeFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                  {quickNoticeFiles.map(f => {
                    const fi = getFileIcon(f.org_file_nm || '');
                    return (
                      <a key={f.file_seq} href={`${API_BASE_URL}/${f.file_path}`} target="_blank" rel="noreferrer" title={f.org_file_nm}
                        style={{ width: 108, flex: '0 0 auto', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 6px', textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>
                        <i className={`fas ${fi.icon}`} style={{ fontSize: 24, color: fi.color }}></i>
                        <div style={{ fontSize: 11, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.org_file_nm}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{formatFileSize(f.file_size || 0)}</div>
                      </a>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 플로팅 상담버튼 - 사용자 메뉴에 1:1상담톡(LINK_KEY='CHAT')이 있을 때만 노출 */}
      <ChatWidget
        enabled={!!chatMenu && (userInfo?.chatReqYn === 'Y' || userInfo?.chatCnslYn === 'Y')}   // 챗데스크 요청/상담 권한(사용자환경설정 Z3020M1)이 있는 사용자에게만 플로팅 버튼 노출
        chatTabOpen={!!chatMenu && openTabs.some(t => t.url === chatMenu.item.url)}
        chatTabActive={!!chatMenu && openTabs.some(t => t.active && t.url === chatMenu.item.url)}
        onOpenTab={openChatTab}
      />

      {/* 일정 알림 토스트 - 사용자 메뉴에 일정관리(LINK_KEY='SCHEDULE')가 있을 때만 동작 */}
      <ScheduleAlert enabled={!!scheduleMenu} onOpenTab={() => openScheduleTab()} />
    </div>
  );
};

export default MainFrame;
